import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Download,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Upload,
  Home as HomeIcon,
  Brain,
  Info,
} from "lucide-react";
import * as XLSX from "xlsx-js-style";

/**
 * ============================================================================
 * LAND PARTITION SYSTEM v7.4c - GLOBAL CONTIGUITY OPTIMIZATION
 * ============================================================================
 *
 * PHILOSOPHY: "Maximum Contiguity, Minimum Fragmentation"
 *
 * v7.4 MAJOR CHANGE:
 *   - Global analysis of ALL blocks before allocation
 *   - Non-possessors assigned to specific blocks for concentrated holdings
 *   - Each shareholder gets land from as few blocks as possible
 *   - Example: Mst Zohidah gets ALL her A1 from Block 3, not scattered pieces
 *
 * ALGORITHM:
 *   1. Analyze all blocks - calculate available area after possessors
 *   2. For each non-possessor, find optimal block assignment
 *   3. Try to satisfy ENTIRE entitlement from single block (ideal)
 *   4. If split needed, minimize number of blocks
 *   5. Execute allocations with pre-determined assignments
 *
 * v7.2/v7.3 FIXES (retained):
 *   - A-grade allocation checks TOTAL entitlement
 *   - Balancing loop can transfer non-locked A-grade
 *   - Remainder logic checks both grade and total room
 *
 * USER INPUT (Only 5 columns):
 *   - Survey_No
 *   - Acres
 *   - Ghontay
 *   - Grade (A1-C)
 *   - Existing_Possessor
 *
 * REMOVED (No longer needed):
 *   - Lock_To_Possessor (auto-detected)
 *   - Bundle_ID (auto-detected)
 *
 * AUTOMATIC RULES:
 *   1. C-GRADE: Always to possessor, never split
 *   2. PRE-SPLIT: Same survey + different possessors = locked to each
 *   3. SAME-SURVEY-SAME-POSSESSOR: Kept together as unit
 *   4. A-GRADE: Possession priority WITHIN entitlement AND total limits
 *   5. B-GRADE: ALWAYS flexible for compensation (never locked)
 *   6. TOTAL SANCTITY: Every shareholder gets exact total entitlement
 *
 * ============================================================================
 */

const PartitionSystem = ({ onBackToLanding }) => {
  // State for multi-step form
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);

  // Grade constants
  const GRADES = ["A1", "A2", "A3", "B1", "B2", "B3", "C"];
  const A_GRADES = ["A1", "A2", "A3"];
  const B_GRADES = ["B1", "B2", "B3"];
  const TOL = 0.001;
  const TOTAL_TOL = 0.0001;
  const GRADE_TOL = 1 / 40; // +/-1 ghonta per A-grade
  const CONTIGUITY_GAP = 2;
  const MAX_ADJUSTMENT_ITER = 5;

  // Dynamic threshold constants
  const ABSOLUTE_FLOOR = 3 / 40;
  const FAIRNESS_MIN = 5 / 40;
  const MAX_STANDARD = 15 / 40;

  // Calculate dynamic PRACTICAL_MIN
  const calculateDynamicPracticalMin = (analysis, shareholderList) => {
    const totalDehArea = Object.values(analysis.totalByGrade).reduce(
      (a, b) => a + b,
      0
    );
    const numShareholders = shareholderList.length;
    const avgShareSize = totalDehArea / numShareholders;
    const dynamicStandard = avgShareSize * 0.04;
    const STANDARD_MIN = Math.max(
      FAIRNESS_MIN,
      Math.min(dynamicStandard, MAX_STANDARD)
    );

    console.log(`\n=== DYNAMIC THRESHOLDS ===`);
    console.log(
      `Total Deh: ${toAG(totalDehArea)}, Shareholders: ${numShareholders}`
    );
    console.log(
      `STANDARD_MIN: ${Math.round(
        STANDARD_MIN * 40
      )} gh, FAIRNESS_MIN: ${Math.round(
        FAIRNESS_MIN * 40
      )} gh, FLOOR: ${Math.round(ABSOLUTE_FLOOR * 40)} gh`
    );

    return {
      ABSOLUTE_FLOOR,
      FAIRNESS_MIN,
      STANDARD_MIN,
      avgShareSize,
      totalDehArea,
    };
  };

  const getAllowableMinimum = (context, dynamicMins) => {
    const { isTopUp, isShortage } = context;
    if (isShortage) return dynamicMins.ABSOLUTE_FLOOR;
    if (isTopUp) return dynamicMins.FAIRNESS_MIN;
    return dynamicMins.STANDARD_MIN;
  };

  // ============================================================================
  // SIMPLIFIED STATE - No Lock/Bundle fields
  // ============================================================================
  const [landData, setLandData] = useState([
    {
      khasraNo: "",
      acres: 0,
      ghontay: 0,
      grade: "A1",
      existingPossessor: "",
    },
  ]);

  const [shareholders, setShareholders] = useState([
    { name: "", sharePercent: 0 },
  ]);

  const [allocationResults, setAllocationResults] = useState(null);

  // Utility functions
  const toAG = (dec) => {
    dec = parseFloat(dec) || 0;
    const acres = Math.floor(dec);
    let gh = Math.round((dec - acres) * 40);
    if (gh === 40) return `${acres + 1}-0`;
    return `${acres}-${gh}`;
  };

  const agToDec = (acres, ghontay) => {
    return parseFloat(acres || 0) + parseFloat(ghontay || 0) / 40.0;
  };

  // ============================================================================
  // SIMPLIFIED EXCEL TEMPLATE - Only 5 columns
  // ============================================================================
  const downloadSampleExcel = () => {
    const wb = XLSX.utils.book_new();

    const headerStyle = {
      fill: { fgColor: { rgb: "0A5F38" } },
      font: { bold: true, color: { rgb: "FFFFFF" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Sheet 1: Land_Data - SIMPLIFIED (5 columns only)
    const landDataSample = [
      ["Survey_No", "Acres", "Ghontay", "Grade", "Existing_Possessor"],
      [17, 8, 2, "A1", "Haji Abdullah"],
      [18, 7, 32, "A1", "Haji Abdullah"],
      [19, 6, 34, "A1", "Haji Abdullah"],
      [20, 5, 0, "A2", "Mst Rukhsana"],
      [20, 2, 28, "A2", "Mst Farzana"],
      [21, 7, 38, "A1", "Haji Abdullah"],
      [22, 5, 5, "A2", "Haji Abdullah"],
      [22, 2, 30, "A2", "Mst Farzana"],
      [106, 5, 14, "A1", "Haji Abdullah"],
      [108, 6, 12, "A1", "Haji Abdullah"],
      [109, 4, 34, "A2", "Haji Abdullah"],
      [110, 5, 17, "A2", "Haji Abdullah"],
      [111, 0, 20, "A2", "Haji Abdullah"],
      [111, 6, 5, "B1", "Haji Abdullah"],
      [113, 7, 10, "A1", "Haji Abdullah"],
      [114, 1, 13, "A1", "Haji Abdullah"],
      [114, 3, 0, "A1", "Mst Rukhsana"],
      [114, 3, 0, "A1", "Mst Farzana"],
      [115, 6, 25, "A1", "Haji Abdullah"],
      [116, 4, 10, "B1", "Haji Abdullah"],
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(landDataSample);
    for (let col = 0; col < 5; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (ws1[cellAddress]) ws1[cellAddress].s = headerStyle;
    }
    ws1["!cols"] = [
      { wch: 12 },
      { wch: 8 },
      { wch: 10 },
      { wch: 8 },
      { wch: 22 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Land_Data");

    // Sheet 2: Shareholders
    const shareholdersSample = [
      ["Shareholder", "Share_%"],
      ["Haji Abdullah", 0.472],
      ["Mst Razia", 0.176],
      ["Mst Rukhsana", 0.176],
      ["Mst Farzana", 0.176],
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(shareholdersSample);
    for (let col = 0; col < 2; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (ws2[cellAddress]) ws2[cellAddress].s = headerStyle;
    }
    ws2["!cols"] = [{ wch: 22 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Shareholders");

    // Sheet 3: Instructions - SIMPLIFIED
    const instructions = [
      ["Land Partition System - Simplified Input Template"],
      [""],
      ["SHEET 1: Land_Data (5 columns only)"],
      [""],
      ["  Survey_No: Survey/Khasra number"],
      ["  Acres: Whole acres (0, 1, 2, etc.)"],
      ["  Ghontay: Fractional part (0-39, where 40 ghontay = 1 acre)"],
      ["  Grade: Land category (A1, A2, A3, B1, B2, B3, or C)"],
      [
        "  Existing_Possessor: Name of current possessor (must match shareholder name exactly)",
      ],
      [""],
      ["AUTOMATIC DETECTION (No user input needed):"],
      [""],
      [
        "  * PRE-SPLIT SURVEYS: If same Survey_No appears with DIFFERENT possessors,",
      ],
      ["    the system automatically locks each part to its possessor."],
      ["    Example: Survey 20 with Rukhsana (5-0) and Farzana (2-28)"],
      [""],
      [
        "  * SAME-POSSESSOR SURVEYS: If same Survey_No appears with SAME possessor",
      ],
      ["    but different grades, the system keeps them together."],
      ["    Example: Survey 111 with Haji (A2: 0-20) and Haji (B1: 6-5)"],
      [""],
      ["  * C-GRADE: Always allocated to existing possessor, never split."],
      [""],
      ["  * B-GRADE: Always flexible for compensation balancing."],
      [""],
      ["SHEET 2: Shareholders"],
      [""],
      ["  Shareholder: Full name (must match Existing_Possessor exactly)"],
      ["  Share_%: Ownership fraction (e.g., 0.25 for 25%, must total 1.0)"],
      [""],
      ["IMPORTANT:"],
      ["  - Possessor names must EXACTLY match shareholder names"],
      ["  - Share percentages must total 1.0 (100%)"],
    ];

    const ws3 = XLSX.utils.aoa_to_sheet(instructions);
    ws3["!cols"] = [{ wch: 90 }];
    XLSX.utils.book_append_sheet(wb, ws3, "Instructions");

    XLSX.writeFile(wb, "Land_Partition_Input_Template.xlsx");
  };

  // ============================================================================
  // SIMPLIFIED FILE UPLOAD - No Lock/Bundle processing
  // ============================================================================
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        let newLandData = [];
        let newShareholders = [];

        // Read Land_Data sheet - SIMPLIFIED (no Lock/Bundle)
        const landSheet = workbook.Sheets["Land_Data"];
        if (landSheet) {
          const landRows = XLSX.utils.sheet_to_json(landSheet);
          newLandData = landRows
            .filter((row) => row.Survey_No || row.survey_no)
            .map((row) => {
              const surveyNo = row.Survey_No || row.survey_no || "";

              let acres = 0;
              let ghontay = 0;

              // Handle various column name formats
              if (row.Acres !== undefined && row.Ghontay !== undefined) {
                acres = parseInt(row.Acres) || 0;
                ghontay = parseInt(row.Ghontay) || 0;
              } else if (
                row.Acres !== undefined &&
                row["Ghontay (0-39)"] !== undefined
              ) {
                acres = parseInt(row.Acres) || 0;
                ghontay = parseInt(row["Ghontay (0-39)"]) || 0;
              }

              const grade = row.Grade || row["Grade (A1-C)"] || "A1";
              const possessor =
                row.Existing_Possessor || row.existing_possessor || "";

              return {
                khasraNo: String(surveyNo),
                acres: acres,
                ghontay: ghontay,
                grade: grade,
                existingPossessor: possessor,
              };
            });

          if (newLandData.length > 0) {
            setLandData(newLandData);
          }
        }

        // Read Shareholders sheet
        const shareholderSheet = workbook.Sheets["Shareholders"];
        if (shareholderSheet) {
          const shareholderRows = XLSX.utils.sheet_to_json(shareholderSheet);
          newShareholders = shareholderRows
            .filter((row) => row.Shareholder && row["Share_%"])
            .map((row) => ({
              name: String(row.Shareholder),
              sharePercent: parseFloat(row["Share_%"]) || 0,
            }));

          if (newShareholders.length > 0) {
            setShareholders(newShareholders);
          }
        }

        alert(
          `File uploaded successfully!\nLoaded ${newLandData.length} surveys and ${newShareholders.length} shareholders.\n\n`
        );
      } catch (error) {
        console.error("Error reading Excel file:", error);
        alert("Error reading Excel file. Please check the format.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // ============================================================================
  // PHASE 0: INTELLIGENT AUTO-DETECTION
  // ============================================================================
  const analyzeProblemSpace = (validLandData, validShareholders) => {
    console.log("\n" + "=".repeat(70));
    console.log("LAND PARTITION SYSTEM v7.4 - AUTOMATIC DETECTION");
    console.log("=".repeat(70));

    const analysis = {
      totalByGrade: {},
      lockedSurveys: [], // C-grade + pre-split A-grades
      lockedArea: {},
      flexibleSurveys: { B3: [], B2: [], B1: [] }, // B-grade ALWAYS flexible
      flexibleCapacity: 0,
      constrainedSurveys: { A1: [], A2: [], A3: [] },
      contiguityBlocks: {},
      entitlements: {},
      preSplitSurveys: {},
      samePossessorSurveys: {}, // Same survey, same possessor, different grades
      shortageAnalysis: {},
      warnings: [],
    };

    // Initialize grade totals
    GRADES.forEach((grade) => {
      analysis.totalByGrade[grade] = 0;
    });

    // ========================================================================
    // STEP 1: DETECT SURVEY PATTERNS
    // ========================================================================
    console.log("\n[STEP 1] Detecting survey patterns...");

    const surveyOccurrences = {};
    validLandData.forEach((survey) => {
      const surveyNo = survey.khasraNo;
      if (!surveyOccurrences[surveyNo]) {
        surveyOccurrences[surveyNo] = [];
      }
      surveyOccurrences[surveyNo].push(survey);
    });

    Object.entries(surveyOccurrences).forEach(([surveyNo, entries]) => {
      if (entries.length > 1) {
        const possessors = entries
          .map((e) => e.existingPossessor)
          .filter((p) => p && p.trim());
        const uniquePossessors = [...new Set(possessors)];
        const grades = [...new Set(entries.map((e) => e.grade))];
        const totalArea = entries.reduce(
          (sum, e) => sum + agToDec(e.acres, e.ghontay),
          0
        );

        if (uniquePossessors.length > 1) {
          // CASE 1: PRE-SPLIT - Different possessors
          console.log(
            `  [PRE-SPLIT] Survey ${surveyNo}: ${entries.length} parts, ${uniquePossessors.length} possessors`
          );
          console.log(`    -> ${uniquePossessors.join(", ")}`);

          analysis.preSplitSurveys[surveyNo] = {
            totalParts: entries.length,
            possessors: uniquePossessors,
            totalArea: totalArea,
            entries: entries.map((entry, idx) => ({
              ...entry,
              isPreSplit: true,
              isLocked: true, // Pre-split A/C grades are locked
              partNumber: idx + 1,
              totalParts: entries.length,
            })),
          };
        } else if (uniquePossessors.length === 1 && uniquePossessors[0]) {
          // CASE 2: SAME-POSSESSOR - Same person, possibly different grades
          const possessor = uniquePossessors[0];
          const hasC = entries.some((e) => e.grade === "C");
          const hasB = entries.some((e) => B_GRADES.includes(e.grade));
          const hasA = entries.some((e) => A_GRADES.includes(e.grade));

          console.log(
            `  [SAME-POSSESSOR] Survey ${surveyNo}: ${entries.length} parts, all ${possessor}`
          );
          console.log(
            `    -> Grades: ${grades.join(", ")}, Total: ${toAG(totalArea)}`
          );

          analysis.samePossessorSurveys[surveyNo] = {
            possessor: possessor,
            totalArea: totalArea,
            grades: grades,
            hasResidential: hasC,
            hasBGrade: hasB,
            hasAGrade: hasA,
            entries: entries.map((entry, idx) => ({
              ...entry,
              isSamePossessorGroup: true,
              // Lock rules:
              // - C-grade: always locked
              // - A-grade in same-possessor group: locked (possession priority)
              // - B-grade: NEVER locked (always flexible)
              isLocked:
                entry.grade === "C" || (A_GRADES.includes(entry.grade) && hasC),
              partNumber: idx + 1,
              totalParts: entries.length,
            })),
          };
        }
      }
    });

    console.log(
      `\nDetected: ${Object.keys(analysis.preSplitSurveys).length} pre-split, ${
        Object.keys(analysis.samePossessorSurveys).length
      } same-possessor groups`
    );

    // ========================================================================
    // STEP 2: CLASSIFY SURVEYS
    // ========================================================================
    console.log("\n[STEP 2] Classifying surveys...");

    validLandData.forEach((survey) => {
      const area = agToDec(survey.acres, survey.ghontay);
      const surveyNo = survey.khasraNo;
      const grade = survey.grade;
      const possessor = survey.existingPossessor;

      // Update grade totals
      analysis.totalByGrade[grade] += area;

      // Check if this survey is part of a special group
      let processedSurvey = { ...survey };
      let isLocked = false;

      // Rule 1: C-grade is ALWAYS locked
      if (grade === "C") {
        isLocked = true;
        processedSurvey.lockReason = "c_grade";
      }
      // Rule 2: Pre-split surveys - A/C grades locked to their possessors
      else if (analysis.preSplitSurveys[surveyNo]) {
        const preSplitInfo = analysis.preSplitSurveys[surveyNo];
        const matchingEntry = preSplitInfo.entries.find(
          (e) =>
            e.existingPossessor === possessor &&
            e.acres === survey.acres &&
            e.ghontay === survey.ghontay &&
            e.grade === survey.grade
        );
        if (matchingEntry) {
          processedSurvey = matchingEntry;
          // Pre-split: Lock A-grades and C-grades, keep B-grades flexible
          // Pre-split: Lock ALL grades - physical division already exists on ground
          isLocked = true;
          processedSurvey.lockReason = "pre_split";
        }
      }
      // Rule 3: Same-possessor groups - special handling
      else if (analysis.samePossessorSurveys[surveyNo]) {
        const groupInfo = analysis.samePossessorSurveys[surveyNo];
        const matchingEntry = groupInfo.entries.find(
          (e) =>
            e.existingPossessor === possessor &&
            e.acres === survey.acres &&
            e.ghontay === survey.ghontay &&
            e.grade === survey.grade
        );
        if (matchingEntry) {
          processedSurvey = matchingEntry;
          // Same-possessor: Lock if C-grade present, otherwise only lock C itself
          // B-grade is ALWAYS flexible regardless of group
          if (grade === "C") {
            isLocked = true;
            processedSurvey.lockReason = "c_grade_in_group";
          } else if (A_GRADES.includes(grade) && groupInfo.hasResidential) {
            // A-grade adjacent to house - keep together
            isLocked = true;
            processedSurvey.lockReason = "adjacent_to_residential";
          }
          // B-grade in same-possessor group: NOT locked, stays flexible
        }
      }

      // Add to appropriate lists
      if (isLocked && possessor) {
        analysis.lockedSurveys.push(processedSurvey);
        analysis.lockedArea[possessor] =
          (analysis.lockedArea[possessor] || 0) + area;
      } else if (B_GRADES.includes(grade)) {
        // B-grade is ALWAYS flexible (never locked)
        analysis.flexibleSurveys[grade].push(processedSurvey);
        analysis.flexibleCapacity += area;
      } else if (A_GRADES.includes(grade)) {
        // A-grade not locked - available for allocation
        analysis.constrainedSurveys[grade].push(processedSurvey);
      }
    });

    // Log classification results
    const totalLocked = Object.values(analysis.lockedArea).reduce(
      (a, b) => a + b,
      0
    );
    console.log(
      `  Locked: ${toAG(totalLocked)} (C-grade + pre-split A-grades)`
    );
    console.log(`  Flexible (B-grade): ${toAG(analysis.flexibleCapacity)}`);
    console.log(
      `  Constrained (A-grade): ${toAG(
        Object.values(analysis.constrainedSurveys).reduce(
          (sum, arr) =>
            sum +
            arr.reduce(
              (s, survey) => s + agToDec(survey.acres, survey.ghontay),
              0
            ),
          0
        )
      )}`
    );

    // ========================================================================
    // STEP 3: BUILD CONTIGUITY BLOCKS
    // ========================================================================
    console.log("\n[STEP 3] Building contiguity blocks...");

    const identifyContiguousBlocks = (surveys) => {
      if (surveys.length === 0) return [];
      const sorted = [...surveys].sort((a, b) => {
        const aNum = parseFloat(a.khasraNo) || 0;
        const bNum = parseFloat(b.khasraNo) || 0;
        return aNum - bNum;
      });

      const blocks = [];
      let currentBlock = [sorted[0]];

      for (let i = 1; i < sorted.length; i++) {
        const prevKhasra = parseFloat(sorted[i - 1].khasraNo) || 0;
        const currKhasra = parseFloat(sorted[i].khasraNo) || 0;
        if (currKhasra - prevKhasra <= CONTIGUITY_GAP) {
          currentBlock.push(sorted[i]);
        } else {
          blocks.push(currentBlock);
          currentBlock = [sorted[i]];
        }
      }
      if (currentBlock.length > 0) blocks.push(currentBlock);
      return blocks;
    };

    for (const grade of [...A_GRADES, ...B_GRADES]) {
      const surveys = grade.startsWith("A")
        ? analysis.constrainedSurveys[grade] || []
        : analysis.flexibleSurveys[grade] || [];
      analysis.contiguityBlocks[grade] = identifyContiguousBlocks(surveys);
    }

    // ========================================================================
    // STEP 4: CALCULATE ENTITLEMENTS
    // ========================================================================
    console.log("\n[STEP 4] Calculating entitlements...");

    const totalDehArea = Object.values(analysis.totalByGrade).reduce(
      (sum, a) => sum + a,
      0
    );
    const shareholderNames = validShareholders.map((sh) => sh.name);

    validShareholders.forEach((sh) => {
      analysis.entitlements[sh.name] = {
        total: totalDehArea * sh.sharePercent,
      };
      GRADES.forEach((grade) => {
        analysis.entitlements[sh.name][grade] =
          analysis.totalByGrade[grade] * sh.sharePercent;
      });
      console.log(
        `  ${sh.name}: ${toAG(analysis.entitlements[sh.name].total)} total (${(
          sh.sharePercent * 100
        ).toFixed(1)}%)`
      );
    });

    // ========================================================================
    // STEP 5: SHORTAGE ANALYSIS
    // ========================================================================
    console.log("\n[STEP 5] Analyzing shortages...");

    A_GRADES.forEach((grade) => {
      const lockedPerShareholder = {};
      shareholderNames.forEach((sh) => {
        lockedPerShareholder[sh] = 0;
      });

      analysis.lockedSurveys.forEach((survey) => {
        if (survey.grade === grade && survey.existingPossessor) {
          const area = agToDec(survey.acres, survey.ghontay);
          if (lockedPerShareholder[survey.existingPossessor] !== undefined) {
            lockedPerShareholder[survey.existingPossessor] += area;
          }
        }
      });

      const totalGradeArea = analysis.totalByGrade[grade];
      const totalLockedInGrade = Object.values(lockedPerShareholder).reduce(
        (a, b) => a + b,
        0
      );
      const remainingAfterLocked = totalGradeArea - totalLockedInGrade;

      let totalStillNeeded = 0;
      const stillNeededPerShareholder = {};

      shareholderNames.forEach((sh) => {
        const entitled = analysis.entitlements[sh][grade];
        const locked = lockedPerShareholder[sh];
        const stillNeeds = Math.max(0, entitled - locked);
        stillNeededPerShareholder[sh] = stillNeeds;
        totalStillNeeded += stillNeeds;
      });

      const shortage = totalStillNeeded - remainingAfterLocked;

      if (shortage > TOL) {
        console.log(`  [SHORTAGE] ${grade}: ${toAG(shortage)} short`);
        analysis.shortageAnalysis[grade] = {
          hasShortage: true,
          shortage: shortage,
          remainingAfterLocked,
          totalStillNeeded,
          lockedPerShareholder,
          stillNeededPerShareholder,
        };
      } else {
        console.log(`  [OK] ${grade}: sufficient`);
        analysis.shortageAnalysis[grade] = {
          hasShortage: false,
          shortage: 0,
          remainingAfterLocked,
          totalStillNeeded,
          lockedPerShareholder,
          stillNeededPerShareholder,
        };
      }
    });

    console.log("\n" + "=".repeat(70));
    return analysis;
  };

  // ============================================================================
  // PHASE 1: BUILD ALLOCATION PLAN
  // ============================================================================

  const planLockedSurveys = (analysis, plan) => {
    const locked = [];
    const lockedSurveyIds = new Set();

    analysis.lockedSurveys.forEach((survey) => {
      const possessor = survey.existingPossessor;
      const area = agToDec(survey.acres, survey.ghontay);

      if (possessor) {
        let surveyDisplay = survey.khasraNo;
        let reason = survey.lockReason || "locked";

        if (survey.isPreSplit) {
          surveyDisplay = `${survey.khasraNo}/${survey.partNumber}`;
          reason = `pre_split_${survey.partNumber}of${survey.totalParts}`;
        } else if (survey.isSamePossessorGroup && survey.totalParts > 1) {
          surveyDisplay = `${survey.khasraNo}/${survey.partNumber}`;
        }

        locked.push({
          survey: surveyDisplay,
          grade: survey.grade,
          shareholder: possessor,
          area: area,
          type: "locked",
          reason: reason,
          isPreSplit: survey.isPreSplit || false,
          partNumber: survey.partNumber,
          totalParts: survey.totalParts,
        });

        lockedSurveyIds.add(survey.khasraNo);
      }
    });

    plan.lockedSurveyIds = lockedSurveyIds;
    return locked;
  };

  const planSingleBlock = (
    block,
    grade,
    shareholderList,
    analysis,
    plan,
    localGradeAllocated,
    dynamicMins
  ) => {
    const lockedSurveyIds = plan.lockedSurveyIds || new Set();
    const unlockedBlock = block.filter((s) => !lockedSurveyIds.has(s.khasraNo));

    if (unlockedBlock.length === 0) return [];

    const blockPlan = [];
    const isAGrade = A_GRADES.includes(grade);
    const shortageInfo = analysis.shortageAnalysis[grade];
    const hasShortage = shortageInfo && shortageInfo.hasShortage;

    const surveyRemaining = {};
    block.forEach((survey) => {
      surveyRemaining[survey.khasraNo] = agToDec(survey.acres, survey.ghontay);
    });

    // Calculate total block area
    const totalBlockArea = unlockedBlock.reduce(
      (sum, s) => sum + surveyRemaining[s.khasraNo],
      0
    );

    // Calculate targets and separate possessors from non-possessors
    const allTargets = shareholderList
      .map((sh) => {
        const gradeEntitled = analysis.entitlements[sh][grade];
        const alreadyAllocated = localGradeAllocated[sh]?.[grade] || 0;
        const maxAllowed = isAGrade
          ? gradeEntitled + GRADE_TOL
          : gradeEntitled + 1000;
        const remaining = maxAllowed - alreadyAllocated;
        const totalGap = analysis.entitlements[sh].total - plan.allocated[sh];
        const isPossessor = unlockedBlock.some(
          (s) => s.existingPossessor === sh
        );
        const isTopUp = remaining > 0 && remaining < dynamicMins.STANDARD_MIN;

        // Calculate total entitlement for total limit check
        const totalEntitled = analysis.entitlements[sh].total;
        const currentTotalAllocated = plan.allocated[sh];
        const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

        if (remaining <= TOL) return null;

        return {
          shareholder: sh,
          target: Math.max(0, Math.min(remaining, totalRoom)), // Cap at BOTH grade and total
          gradeEntitled,
          remaining,
          alreadyAllocated,
          totalGap,
          totalRoom,
          isPossessor,
          isTopUp,
        };
      })
      .filter((t) => t !== null && t.target > TOL);

    // Split into possessors and non-possessors
    const possessors = allTargets.filter((t) => t.isPossessor);
    const nonPossessors = allTargets.filter((t) => !t.isPossessor);

    // Sort possessors by remaining need (biggest need first)
    possessors.sort((a, b) => b.remaining - a.remaining);

    let surveyIndex = 0;

    // ========================================================================
    // PHASE 1: Allocate to possessors first (they get priority)
    // ========================================================================
    for (const target of possessors) {
      if (surveyIndex >= unlockedBlock.length) break;
      if (target.target <= TOL) continue;

      let allocated = 0;

      while (
        allocated < target.target - TOL &&
        surveyIndex < unlockedBlock.length
      ) {
        const survey = unlockedBlock[surveyIndex];
        const surveyKey = survey.khasraNo;
        const surveyArea = surveyRemaining[surveyKey];

        if (surveyArea <= TOL) {
          surveyIndex++;
          continue;
        }

        // Check current allocation
        const currentAllocated =
          localGradeAllocated[target.shareholder]?.[grade] || 0;
        const inBlockAllocated = blockPlan
          .filter(
            (a) => a.shareholder === target.shareholder && a.grade === grade
          )
          .reduce((sum, a) => sum + a.area, 0);
        const totalAllocatedSoFar = currentAllocated + inBlockAllocated;
        const entitled = analysis.entitlements[target.shareholder][grade];

        // Check total allocation
        const totalBlockAllocated = blockPlan
          .filter((a) => a.shareholder === target.shareholder)
          .reduce((sum, a) => sum + a.area, 0);
        const currentTotalAllocated =
          plan.allocated[target.shareholder] + totalBlockAllocated;
        const totalEntitled = analysis.entitlements[target.shareholder].total;
        const totalRemaining =
          totalEntitled - currentTotalAllocated + GRADE_TOL;

        // Check both grade and total limits
        let maxCanTake = isAGrade
          ? Math.max(
              0,
              Math.min(
                entitled + GRADE_TOL - totalAllocatedSoFar,
                totalRemaining
              )
            )
          : Math.max(0, totalRemaining);

        if (maxCanTake <= TOL) break;

        const needed = Math.min(
          target.target - allocated,
          maxCanTake,
          surveyArea
        );
        const context = { isTopUp: target.isTopUp, isShortage: hasShortage };
        const minThreshold = getAllowableMinimum(context, dynamicMins);

        if (surveyArea <= needed + TOL) {
          blockPlan.push({
            survey: survey.khasraNo,
            grade,
            shareholder: target.shareholder,
            area: surveyArea,
            type: "whole",
            reason: "possessor_allocation",
          });
          allocated += surveyArea;
          surveyRemaining[surveyKey] = 0;
          surveyIndex++;
        } else {
          const give = needed;
          const remainder = surveyArea - give;
          const giveSafe = give >= minThreshold;
          const remainderSafe = remainder >= minThreshold;

          if (giveSafe && remainderSafe) {
            blockPlan.push({
              survey: survey.khasraNo,
              grade,
              shareholder: target.shareholder,
              area: give,
              type: "split",
              reason: "possessor_split",
            });
            allocated += give;
            surveyRemaining[surveyKey] -= give;
          } else if (giveSafe && !remainderSafe) {
            // Check limits before avoid_micro
            const wouldGradeTotal = totalAllocatedSoFar + surveyArea;
            const wouldTotalAlloc = currentTotalAllocated + surveyArea;
            if (wouldGradeTotal > entitled + GRADE_TOL + TOL) break;
            if (wouldTotalAlloc > totalEntitled + GRADE_TOL + TOL) break;

            blockPlan.push({
              survey: survey.khasraNo,
              grade,
              shareholder: target.shareholder,
              area: surveyArea,
              type: "whole",
              reason: "possessor_avoid_micro",
            });
            allocated += surveyArea;
            surveyRemaining[surveyKey] = 0;
            surveyIndex++;
          } else {
            break;
          }
        }
      }
    }

    // ========================================================================
    // PHASE 2: Calculate remaining area and distribute PROPORTIONALLY to non-possessors
    // ========================================================================
    let remainingArea = 0;
    for (const survey of unlockedBlock) {
      remainingArea += surveyRemaining[survey.khasraNo];
    }

    if (remainingArea > TOL && nonPossessors.length > 0) {
      // Calculate each non-possessor's fair share based on their GRADE entitlement
      const totalNonPossessorNeed = nonPossessors.reduce(
        (sum, t) => sum + t.target,
        0
      );

      // Calculate proportional fair share for each
      const fairShares = nonPossessors.map((t) => ({
        ...t,
        fairShare:
          remainingArea >= totalNonPossessorNeed
            ? t.target // Enough for everyone
            : remainingArea * (t.target / totalNonPossessorNeed), // Proportional
      }));

      // Sort by fairShare descending (biggest share first to minimize micro-splits)
      fairShares.sort((a, b) => b.fairShare - a.fairShare);

      // Distribute surveys to each non-possessor based on their fair share
      for (const target of fairShares) {
        if (target.fairShare <= TOL) continue;

        let allocated = 0;
        let targetAllocation = target.fairShare;

        // Find surveys to give to this shareholder
        for (
          let i = 0;
          i < unlockedBlock.length && allocated < targetAllocation - TOL;
          i++
        ) {
          const survey = unlockedBlock[i];
          const surveyKey = survey.khasraNo;
          const surveyArea = surveyRemaining[surveyKey];

          if (surveyArea <= TOL) continue;

          // Check current allocation
          const currentAllocated =
            localGradeAllocated[target.shareholder]?.[grade] || 0;
          const inBlockAllocated = blockPlan
            .filter(
              (a) => a.shareholder === target.shareholder && a.grade === grade
            )
            .reduce((sum, a) => sum + a.area, 0);
          const totalAllocatedSoFar = currentAllocated + inBlockAllocated;
          const entitled = analysis.entitlements[target.shareholder][grade];

          // Check total allocation
          const totalBlockAllocated = blockPlan
            .filter((a) => a.shareholder === target.shareholder)
            .reduce((sum, a) => sum + a.area, 0);
          const currentTotalAllocated =
            plan.allocated[target.shareholder] + totalBlockAllocated;
          const totalEntitled = analysis.entitlements[target.shareholder].total;
          const totalRemaining =
            totalEntitled - currentTotalAllocated + GRADE_TOL;

          // Check both grade and total limits
          let maxCanTake = isAGrade
            ? Math.max(
                0,
                Math.min(
                  entitled + GRADE_TOL - totalAllocatedSoFar,
                  totalRemaining
                )
              )
            : Math.max(0, totalRemaining);

          if (maxCanTake <= TOL) continue;

          const needed = Math.min(
            targetAllocation - allocated,
            maxCanTake,
            surveyArea
          );
          const context = { isTopUp: target.isTopUp, isShortage: hasShortage };
          const minThreshold = getAllowableMinimum(context, dynamicMins);

          if (surveyArea <= needed + TOL) {
            blockPlan.push({
              survey: survey.khasraNo,
              grade,
              shareholder: target.shareholder,
              area: surveyArea,
              type: "whole",
              reason: "fair_share_allocation",
            });
            allocated += surveyArea;
            surveyRemaining[surveyKey] = 0;
          } else {
            const give = needed;
            const remainder = surveyArea - give;
            const giveSafe = give >= minThreshold;
            const remainderSafe = remainder >= minThreshold;

            if (giveSafe && remainderSafe) {
              blockPlan.push({
                survey: survey.khasraNo,
                grade,
                shareholder: target.shareholder,
                area: give,
                type: "split",
                reason: "fair_share_split",
              });
              allocated += give;
              surveyRemaining[surveyKey] -= give;
            } else if (
              surveyArea <=
              targetAllocation - allocated + minThreshold
            ) {
              // Take whole if it roughly fits
              const wouldGradeTotal = totalAllocatedSoFar + surveyArea;
              const wouldTotalAlloc = currentTotalAllocated + surveyArea;
              if (
                wouldGradeTotal <= entitled + GRADE_TOL + TOL &&
                wouldTotalAlloc <= totalEntitled + GRADE_TOL + TOL
              ) {
                blockPlan.push({
                  survey: survey.khasraNo,
                  grade,
                  shareholder: target.shareholder,
                  area: surveyArea,
                  type: "whole",
                  reason: "fair_share_whole",
                });
                allocated += surveyArea;
                surveyRemaining[surveyKey] = 0;
              }
            }
          }
        }
      }
    }

    // ========================================================================
    // PHASE 3: Handle any truly remaining surveys (edge cases)
    // ========================================================================
    for (let i = 0; i < unlockedBlock.length; i++) {
      const survey = unlockedBlock[i];
      const surveyArea = surveyRemaining[survey.khasraNo];

      if (surveyArea <= TOL) continue;

      // Find who can take this survey (has room in BOTH grade and total)
      const candidates = shareholderList
        .map((sh) => {
          const currentGradeAllocated = localGradeAllocated[sh]?.[grade] || 0;
          const inBlockGradeAllocated = blockPlan
            .filter((a) => a.shareholder === sh && a.grade === grade)
            .reduce((sum, a) => sum + a.area, 0);
          const totalGradeAllocated =
            currentGradeAllocated + inBlockGradeAllocated;
          const gradeEntitled = analysis.entitlements[sh][grade];
          const gradeRoom = gradeEntitled + GRADE_TOL - totalGradeAllocated;
          const gradeGap = gradeEntitled - totalGradeAllocated;

          const inBlockTotalAllocated = blockPlan
            .filter((a) => a.shareholder === sh)
            .reduce((sum, a) => sum + a.area, 0);
          const currentTotalAllocated =
            plan.allocated[sh] + inBlockTotalAllocated;
          const totalEntitled = analysis.entitlements[sh].total;
          const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

          return {
            shareholder: sh,
            gradeRoom,
            gradeGap,
            totalRoom,
            canTake: gradeRoom > TOL && totalRoom > TOL,
          };
        })
        .filter((c) => c.canTake);

      if (candidates.length === 0) {
        console.log(
          `  [WARNING] No one can take remainder survey ${survey.khasraNo} (${grade})`
        );
        continue;
      }

      // Sort by grade gap (who needs this grade most)
      candidates.sort((a, b) => b.gradeGap - a.gradeGap);
      const recipient = candidates[0];

      const maxCanTake = Math.min(
        recipient.gradeRoom,
        recipient.totalRoom,
        surveyArea
      );

      if (maxCanTake >= surveyArea - TOL) {
        blockPlan.push({
          survey: survey.khasraNo,
          grade,
          shareholder: recipient.shareholder,
          area: surveyArea,
          type: "whole",
          reason: "remainder_to_neediest",
        });
        surveyRemaining[survey.khasraNo] = 0;
      } else if (maxCanTake >= ABSOLUTE_FLOOR) {
        blockPlan.push({
          survey: survey.khasraNo,
          grade,
          shareholder: recipient.shareholder,
          area: maxCanTake,
          type: "split",
          reason: "remainder_partial",
        });
        surveyRemaining[survey.khasraNo] -= maxCanTake;
        i--; // Re-process this survey for next candidate
      }
    }

    return blockPlan;
  };

  const updatePlanTracking = (plan, allocations) => {
    allocations.forEach((alloc) => {
      plan.allocated[alloc.shareholder] += alloc.area;
      plan.allocatedByGrade[alloc.shareholder][alloc.grade] += alloc.area;
    });
  };

  // ============================================================================
  // v7.4: GLOBAL CONTIGUITY OPTIMIZATION
  // ============================================================================
  // Instead of distributing each block to all shareholders (fragmenting),
  // we analyze ALL blocks first and assign non-possessors to specific blocks
  // where they can get concentrated holdings.
  // ============================================================================

  const planAGradeSurveys = (analysis, shareholderList, plan, dynamicMins) => {
    const aGradePlan = [];
    const localGradeAllocated = {};

    shareholderList.forEach((sh) => {
      localGradeAllocated[sh] = {};
      A_GRADES.forEach((g) => {
        localGradeAllocated[sh][g] = plan.allocatedByGrade[sh][g] || 0;
      });
    });

    console.log(
      "\n[A-GRADE ALLOCATION - GLOBAL CONTIGUITY OPTIMIZATION v7.4c]"
    );

    for (const grade of A_GRADES) {
      const blocks = analysis.contiguityBlocks[grade] || [];
      console.log(
        `\n  ${grade}: ${blocks.length} blocks - analyzing globally...`
      );

      if (blocks.length === 0) continue;

      const lockedSurveyIds = plan.lockedSurveyIds || new Set();

      // ======================================================================
      // PASS 1: Calculate TOTAL possession per shareholder across ALL blocks
      // ======================================================================
      const totalPossessionByShareHolder = {};
      const possessionByBlock = {}; // sh -> blockIndex -> area

      shareholderList.forEach((sh) => {
        totalPossessionByShareHolder[sh] = 0;
        possessionByBlock[sh] = {};
      });

      blocks.forEach((block, blockIndex) => {
        const unlockedSurveys = block.filter(
          (s) => !lockedSurveyIds.has(s.khasraNo)
        );

        shareholderList.forEach((sh) => {
          const possessedArea = unlockedSurveys
            .filter((s) => s.existingPossessor === sh)
            .reduce((sum, s) => sum + agToDec(s.acres, s.ghontay), 0);

          if (possessedArea > TOL) {
            totalPossessionByShareHolder[sh] += possessedArea;
            possessionByBlock[sh][blockIndex] = possessedArea;
          }
        });
      });

      // Log possession analysis
      console.log("    Possession analysis:");
      shareholderList.forEach((sh) => {
        if (totalPossessionByShareHolder[sh] > TOL) {
          const entitled = analysis.entitlements[sh][grade];
          const excess = totalPossessionByShareHolder[sh] - entitled;
          console.log(
            `      ${sh}: possesses ${toAG(
              totalPossessionByShareHolder[sh]
            )}, entitled ${toAG(entitled)}, excess ${toAG(Math.max(0, excess))}`
          );
        }
      });

      // ======================================================================
      // PASS 2: Calculate how much each possessor will ACTUALLY claim
      // (limited by their entitlement, not their possession)
      // ======================================================================
      const actualClaimByBlock = {}; // blockIndex -> { sh -> claimArea }
      const remainingEntitlement = {}; // sh -> how much they still need

      shareholderList.forEach((sh) => {
        const entitled = analysis.entitlements[sh][grade];
        const alreadyAllocated = localGradeAllocated[sh][grade] || 0;
        remainingEntitlement[sh] = Math.max(
          0,
          entitled + GRADE_TOL - alreadyAllocated
        );
      });

      // Process blocks to determine claims (possessors get their land up to entitlement)
      blocks.forEach((block, blockIndex) => {
        actualClaimByBlock[blockIndex] = {};

        shareholderList.forEach((sh) => {
          const possessedHere = possessionByBlock[sh][blockIndex] || 0;
          if (possessedHere > TOL && remainingEntitlement[sh] > TOL) {
            // This possessor can claim up to min(possessed, remaining entitlement)
            const claim = Math.min(possessedHere, remainingEntitlement[sh]);
            actualClaimByBlock[blockIndex][sh] = claim;
            remainingEntitlement[sh] -= claim; // Reduce for next blocks
          }
        });
      });

      // ======================================================================
      // STEP 1: Build block analysis with CORRECT available area
      // ======================================================================
      const blockAnalysis = blocks.map((block, blockIndex) => {
        const unlockedSurveys = block.filter(
          (s) => !lockedSurveyIds.has(s.khasraNo)
        );

        const totalArea = unlockedSurveys.reduce(
          (sum, s) => sum + agToDec(s.acres, s.ghontay),
          0
        );

        // Get possessors and their claims for this block
        const possessorsInBlock = Object.keys(
          actualClaimByBlock[blockIndex] || {}
        );
        const possessorClaims = actualClaimByBlock[blockIndex] || {};

        // Calculate actual area that will go to possessors
        const estimatedPossessorArea = Object.values(possessorClaims).reduce(
          (sum, v) => sum + v,
          0
        );
        const availableForNonPossessors = Math.max(
          0,
          totalArea - estimatedPossessorArea
        );

        // Get survey number range for contiguity info
        const surveyNums = unlockedSurveys
          .map((s) => parseInt(s.khasraNo) || 0)
          .sort((a, b) => a - b);
        const surveyRange =
          surveyNums.length > 0
            ? `${surveyNums[0]}-${surveyNums[surveyNums.length - 1]}`
            : "empty";

        return {
          blockIndex,
          block,
          unlockedSurveys,
          totalArea,
          possessorsInBlock,
          possessorClaims,
          estimatedPossessorArea,
          availableForNonPossessors,
          surveyRange,
          surveys: unlockedSurveys,
        };
      });

      // Log block analysis
      blockAnalysis.forEach((ba) => {
        const claimDetails = Object.entries(ba.possessorClaims)
          .map(([sh, amt]) => `${sh.split(" ")[0]}:${toAG(amt)}`)
          .join(", ");
        console.log(
          `    Block ${ba.blockIndex + 1} (surveys ${ba.surveyRange}): ${toAG(
            ba.totalArea
          )} total, possessor claims: ${
            claimDetails || "none"
          }, available: ${toAG(ba.availableForNonPossessors)}`
        );
      });

      // ======================================================================
      // STEP 2: Identify non-possessors and their needs
      // ======================================================================
      const nonPossessors = shareholderList
        .filter((sh) => {
          // Check if possessor in ANY block of this grade
          const isPossessorAnywhere = blockAnalysis.some((ba) =>
            ba.possessorsInBlock.includes(sh)
          );
          return !isPossessorAnywhere;
        })
        .map((sh) => {
          const gradeEntitled = analysis.entitlements[sh][grade];
          const alreadyAllocated = localGradeAllocated[sh][grade] || 0;
          const stillNeeds = Math.max(
            0,
            gradeEntitled + GRADE_TOL - alreadyAllocated
          );

          const totalEntitled = analysis.entitlements[sh].total;
          const currentTotalAllocated = plan.allocated[sh];
          const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

          return {
            shareholder: sh,
            gradeEntitled,
            stillNeeds: Math.min(stillNeeds, totalRoom),
            totalRoom,
          };
        })
        .filter((np) => np.stillNeeds > TOL);

      console.log(
        `    Non-possessors needing ${grade}: ${
          nonPossessors
            .map((np) => `${np.shareholder}(${toAG(np.stillNeeds)})`)
            .join(", ") || "none"
        }`
      );

      // ======================================================================
      // STEP 3: Assign non-possessors to blocks for maximum contiguity
      // ======================================================================
      // Strategy: Try to give each non-possessor their ENTIRE entitlement from
      // as few blocks as possible, preferring larger blocks.

      const blockAssignments = {}; // blockIndex -> [{ shareholder, targetArea }]
      blockAnalysis.forEach((ba, idx) => {
        blockAssignments[idx] = [];
      });

      // Sort non-possessors by need (smallest first - easier to place in one block)
      const sortedNonPossessors = [...nonPossessors].sort(
        (a, b) => a.stillNeeds - b.stillNeeds
      );

      // Track remaining available area per block
      const blockRemaining = {};
      blockAnalysis.forEach((ba, idx) => {
        blockRemaining[idx] = ba.availableForNonPossessors;
      });

      // Assign each non-possessor to blocks
      for (const np of sortedNonPossessors) {
        let remaining = np.stillNeeds;

        // Sort blocks by available area (largest first) for this assignment
        const sortedBlocks = blockAnalysis
          .map((ba, idx) => ({ idx, available: blockRemaining[idx] }))
          .filter((b) => b.available > TOL)
          .sort((a, b) => b.available - a.available);

        // Try to satisfy from fewest blocks possible
        for (const blockInfo of sortedBlocks) {
          if (remaining <= TOL) break;

          const canGetHere = Math.min(remaining, blockInfo.available);

          if (canGetHere > TOL) {
            // Check if this would be the only block for this shareholder (ideal)
            // or if we need to split across blocks

            if (canGetHere >= remaining - TOL) {
              // Can satisfy entirely from this block - IDEAL!
              blockAssignments[blockInfo.idx].push({
                shareholder: np.shareholder,
                targetArea: remaining,
                isComplete: true,
              });
              blockRemaining[blockInfo.idx] -= remaining;
              remaining = 0;
              console.log(
                `    -> ${np.shareholder} assigned entirely to Block ${
                  blockInfo.idx + 1
                } (${toAG(np.stillNeeds)})`
              );
            } else {
              // Need to split - take what we can from this block
              blockAssignments[blockInfo.idx].push({
                shareholder: np.shareholder,
                targetArea: canGetHere,
                isComplete: false,
              });
              blockRemaining[blockInfo.idx] -= canGetHere;
              remaining -= canGetHere;
              console.log(
                `    -> ${np.shareholder} gets ${toAG(canGetHere)} from Block ${
                  blockInfo.idx + 1
                } (partial)`
              );
            }
          }
        }

        if (remaining > TOL) {
          console.log(
            `    [WARNING] ${np.shareholder} still needs ${toAG(
              remaining
            )} ${grade} but no blocks available`
          );
        }
      }

      // ======================================================================
      // STEP 4: Execute allocations block by block
      // ======================================================================
      for (let blockIdx = 0; blockIdx < blockAnalysis.length; blockIdx++) {
        const ba = blockAnalysis[blockIdx];
        const assignments = blockAssignments[blockIdx];

        if (ba.unlockedSurveys.length === 0) continue;

        const blockPlan = executeBlockAllocation(
          ba,
          assignments,
          grade,
          shareholderList,
          analysis,
          plan,
          localGradeAllocated,
          dynamicMins
        );

        aGradePlan.push(...blockPlan);

        // Update local tracking
        blockPlan.forEach((alloc) => {
          localGradeAllocated[alloc.shareholder][alloc.grade] += alloc.area;
        });
      }
    }

    return aGradePlan;
  };

  // ============================================================================
  // Execute allocation for a single block with pre-determined assignments
  // ============================================================================
  const executeBlockAllocation = (
    blockAnalysis,
    nonPossessorAssignments,
    grade,
    shareholderList,
    analysis,
    plan,
    localGradeAllocated,
    dynamicMins
  ) => {
    const { block, unlockedSurveys, possessorsInBlock, possessorClaims } =
      blockAnalysis;
    const blockPlan = [];
    const isAGrade = A_GRADES.includes(grade);
    const shortageInfo = analysis.shortageAnalysis[grade];
    const hasShortage = shortageInfo && shortageInfo.hasShortage;

    const surveyRemaining = {};
    unlockedSurveys.forEach((survey) => {
      surveyRemaining[survey.khasraNo] = agToDec(survey.acres, survey.ghontay);
    });

    // ========================================================================
    // PHASE 1: Allocate to possessors first
    // ========================================================================
    for (const possessor of possessorsInBlock) {
      const target = possessorClaims[possessor] || 0;
      if (target <= TOL) continue;

      let allocated = 0;

      for (const survey of unlockedSurveys) {
        if (allocated >= target - TOL) break;

        const surveyKey = survey.khasraNo;
        const surveyArea = surveyRemaining[surveyKey];
        if (surveyArea <= TOL) continue;

        // Check current allocations
        const currentGradeAllocated =
          localGradeAllocated[possessor]?.[grade] || 0;
        const inBlockAllocated = blockPlan
          .filter((a) => a.shareholder === possessor && a.grade === grade)
          .reduce((sum, a) => sum + a.area, 0);
        const totalGradeAllocated = currentGradeAllocated + inBlockAllocated;
        const gradeEntitled = analysis.entitlements[possessor][grade];
        const gradeRoom = gradeEntitled + GRADE_TOL - totalGradeAllocated;

        const totalBlockAllocated = blockPlan
          .filter((a) => a.shareholder === possessor)
          .reduce((sum, a) => sum + a.area, 0);
        const currentTotalAllocated =
          plan.allocated[possessor] + totalBlockAllocated;
        const totalEntitled = analysis.entitlements[possessor].total;
        const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

        const maxCanTake = Math.max(0, Math.min(gradeRoom, totalRoom));
        if (maxCanTake <= TOL) break;

        const needed = Math.min(target - allocated, maxCanTake, surveyArea);
        // FIX: If possessor only needs a small amount (< STANDARD_MIN), treat as top-up
        const isTopUp = target < dynamicMins.STANDARD_MIN;
        const context = { isTopUp: isTopUp, isShortage: hasShortage };
        const minThreshold = getAllowableMinimum(context, dynamicMins);

        if (surveyArea <= needed + TOL) {
          // Take whole survey
          blockPlan.push({
            survey: surveyKey,
            grade,
            shareholder: possessor,
            area: surveyArea,
            type: "whole",
            reason: "possessor_allocation",
          });
          allocated += surveyArea;
          surveyRemaining[surveyKey] = 0;
        } else if (
          needed >= minThreshold &&
          surveyArea - needed >= minThreshold
        ) {
          // Safe split
          blockPlan.push({
            survey: surveyKey,
            grade,
            shareholder: possessor,
            area: needed,
            type: "split",
            reason: "possessor_split",
          });
          allocated += needed;
          surveyRemaining[surveyKey] -= needed;
        } else if (surveyArea <= target - allocated + GRADE_TOL) {
          // Avoid micro-split by taking whole
          const wouldGradeTotal = totalGradeAllocated + surveyArea;
          const wouldTotalAlloc = currentTotalAllocated + surveyArea;
          if (
            wouldGradeTotal <= gradeEntitled + GRADE_TOL + TOL &&
            wouldTotalAlloc <= totalEntitled + GRADE_TOL + TOL
          ) {
            blockPlan.push({
              survey: surveyKey,
              grade,
              shareholder: possessor,
              area: surveyArea,
              type: "whole",
              reason: "possessor_avoid_micro",
            });
            allocated += surveyArea;
            surveyRemaining[surveyKey] = 0;
          }
        } else if (needed >= ABSOLUTE_FLOOR && needed < minThreshold) {
          // FIX v7.4c: Small split needed for possessor's entitlement
          // If the split is above ABSOLUTE_FLOOR, allow it even if below minThreshold
          // This prevents leaving land unallocated when possessor needs just a small amount
          const remainder = surveyArea - needed;
          if (remainder >= ABSOLUTE_FLOOR) {
            blockPlan.push({
              survey: surveyKey,
              grade,
              shareholder: possessor,
              area: needed,
              type: "split",
              reason: "possessor_small_entitlement",
            });
            allocated += needed;
            surveyRemaining[surveyKey] -= needed;
          }
        }
      }
    }

    // ========================================================================
    // PHASE 2: Allocate to assigned non-possessors
    // ========================================================================
    for (const assignment of nonPossessorAssignments) {
      const { shareholder, targetArea } = assignment;
      if (targetArea <= TOL) continue;

      let allocated = 0;

      for (const survey of unlockedSurveys) {
        if (allocated >= targetArea - TOL) break;

        const surveyKey = survey.khasraNo;
        const surveyArea = surveyRemaining[surveyKey];
        if (surveyArea <= TOL) continue;

        // Check current allocations
        const currentGradeAllocated =
          localGradeAllocated[shareholder]?.[grade] || 0;
        const inBlockAllocated = blockPlan
          .filter((a) => a.shareholder === shareholder && a.grade === grade)
          .reduce((sum, a) => sum + a.area, 0);
        const totalGradeAllocated = currentGradeAllocated + inBlockAllocated;
        const gradeEntitled = analysis.entitlements[shareholder][grade];
        const gradeRoom = gradeEntitled + GRADE_TOL - totalGradeAllocated;

        const totalBlockAllocated = blockPlan
          .filter((a) => a.shareholder === shareholder)
          .reduce((sum, a) => sum + a.area, 0);
        const currentTotalAllocated =
          plan.allocated[shareholder] + totalBlockAllocated;
        const totalEntitled = analysis.entitlements[shareholder].total;
        const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

        const maxCanTake = Math.max(0, Math.min(gradeRoom, totalRoom));
        if (maxCanTake <= TOL) break;

        const needed = Math.min(targetArea - allocated, maxCanTake, surveyArea);
        // FIX: If non-possessor only needs a small remaining amount, treat as top-up
        const remainingNeed = targetArea - allocated;
        const isTopUp = remainingNeed < dynamicMins.STANDARD_MIN;
        const context = { isTopUp: isTopUp, isShortage: hasShortage };
        const minThreshold = getAllowableMinimum(context, dynamicMins);

        if (surveyArea <= needed + TOL) {
          blockPlan.push({
            survey: surveyKey,
            grade,
            shareholder: shareholder,
            area: surveyArea,
            type: "whole",
            reason: "global_contiguity_allocation",
          });
          allocated += surveyArea;
          surveyRemaining[surveyKey] = 0;
        } else if (
          needed >= minThreshold &&
          surveyArea - needed >= minThreshold
        ) {
          blockPlan.push({
            survey: surveyKey,
            grade,
            shareholder: shareholder,
            area: needed,
            type: "split",
            reason: "global_contiguity_split",
          });
          allocated += needed;
          surveyRemaining[surveyKey] -= needed;
        } else if (surveyArea <= targetArea - allocated + GRADE_TOL) {
          const wouldGradeTotal = totalGradeAllocated + surveyArea;
          const wouldTotalAlloc = currentTotalAllocated + surveyArea;
          if (
            wouldGradeTotal <= gradeEntitled + GRADE_TOL + TOL &&
            wouldTotalAlloc <= totalEntitled + GRADE_TOL + TOL
          ) {
            blockPlan.push({
              survey: surveyKey,
              grade,
              shareholder: shareholder,
              area: surveyArea,
              type: "whole",
              reason: "global_contiguity_whole",
            });
            allocated += surveyArea;
            surveyRemaining[surveyKey] = 0;
          }
        } else if (needed >= ABSOLUTE_FLOOR && needed < minThreshold) {
          // FIX v7.4c: Small split above ABSOLUTE_FLOOR
          const remainder = surveyArea - needed;
          if (remainder >= ABSOLUTE_FLOOR) {
            blockPlan.push({
              survey: surveyKey,
              grade,
              shareholder: shareholder,
              area: needed,
              type: "split",
              reason: "small_entitlement_split",
            });
            allocated += needed;
            surveyRemaining[surveyKey] -= needed;
          }
        }
      }
    }

    // ========================================================================
    // PHASE 3: Handle any remaining surveys
    // ========================================================================
    for (const survey of unlockedSurveys) {
      const surveyKey = survey.khasraNo;
      const surveyArea = surveyRemaining[surveyKey];

      if (surveyArea <= TOL) continue;

      // Find who can take this survey
      const candidates = shareholderList
        .map((sh) => {
          const currentGradeAllocated = localGradeAllocated[sh]?.[grade] || 0;
          const inBlockGradeAllocated = blockPlan
            .filter((a) => a.shareholder === sh && a.grade === grade)
            .reduce((sum, a) => sum + a.area, 0);
          const totalGradeAllocated =
            currentGradeAllocated + inBlockGradeAllocated;
          const gradeEntitled = analysis.entitlements[sh][grade];
          const gradeRoom = gradeEntitled + GRADE_TOL - totalGradeAllocated;
          const gradeGap = gradeEntitled - totalGradeAllocated;

          const inBlockTotalAllocated = blockPlan
            .filter((a) => a.shareholder === sh)
            .reduce((sum, a) => sum + a.area, 0);
          const currentTotalAllocated =
            plan.allocated[sh] + inBlockTotalAllocated;
          const totalEntitled = analysis.entitlements[sh].total;
          const totalRoom = totalEntitled + GRADE_TOL - currentTotalAllocated;

          return {
            shareholder: sh,
            gradeRoom,
            gradeGap,
            totalRoom,
            canTake: gradeRoom > TOL && totalRoom > TOL,
          };
        })
        .filter((c) => c.canTake);

      if (candidates.length === 0) {
        console.log(
          `    [WARNING] No one can take remainder survey ${surveyKey} (${grade})`
        );
        continue;
      }

      // Sort by grade gap (who needs this grade most)
      candidates.sort((a, b) => b.gradeGap - a.gradeGap);
      const recipient = candidates[0];

      const maxCanTake = Math.min(
        recipient.gradeRoom,
        recipient.totalRoom,
        surveyArea
      );

      if (maxCanTake >= surveyArea - TOL) {
        blockPlan.push({
          survey: surveyKey,
          grade,
          shareholder: recipient.shareholder,
          area: surveyArea,
          type: "whole",
          reason: "remainder_to_neediest",
        });
        surveyRemaining[surveyKey] = 0;
      } else if (maxCanTake >= ABSOLUTE_FLOOR) {
        blockPlan.push({
          survey: surveyKey,
          grade,
          shareholder: recipient.shareholder,
          area: maxCanTake,
          type: "split",
          reason: "remainder_partial",
        });
        surveyRemaining[surveyKey] -= maxCanTake;
      }
    }

    // ========================================================================
    // PHASE 4: Final sweep - allocate any truly remaining land
    // ========================================================================
    let anyRemaining = Object.values(surveyRemaining).some((v) => v > TOL);
    while (anyRemaining) {
      anyRemaining = false;

      for (const survey of unlockedSurveys) {
        const surveyKey = survey.khasraNo;
        const surveyArea = surveyRemaining[surveyKey];

        if (surveyArea <= TOL) continue;
        anyRemaining = true;

        // Find anyone who can take any amount
        const candidates = shareholderList
          .map((sh) => {
            const currentGradeAllocated = localGradeAllocated[sh]?.[grade] || 0;
            const inBlockGradeAllocated = blockPlan
              .filter((a) => a.shareholder === sh && a.grade === grade)
              .reduce((sum, a) => sum + a.area, 0);
            const totalGradeAllocated =
              currentGradeAllocated + inBlockGradeAllocated;
            const gradeEntitled = analysis.entitlements[sh][grade];
            const gradeGap = gradeEntitled - totalGradeAllocated;

            const inBlockTotalAllocated = blockPlan
              .filter((a) => a.shareholder === sh)
              .reduce((sum, a) => sum + a.area, 0);
            const currentTotalAllocated =
              plan.allocated[sh] + inBlockTotalAllocated;
            const totalEntitled = analysis.entitlements[sh].total;
            const totalGap = totalEntitled - currentTotalAllocated;

            return {
              shareholder: sh,
              gradeGap,
              totalGap,
              canTake: totalGap > TOL,
            };
          })
          .filter((c) => c.canTake);

        if (candidates.length === 0) {
          console.log(
            `    [FINAL] Survey ${surveyKey} has ${toAG(
              surveyArea
            )} remaining but no one can take it`
          );
          surveyRemaining[surveyKey] = 0;
          continue;
        }

        // Sort by who needs most overall
        candidates.sort((a, b) => b.totalGap - a.totalGap);
        const recipient = candidates[0];

        const giveAmount = Math.min(surveyArea, recipient.totalGap);

        if (giveAmount >= ABSOLUTE_FLOOR) {
          blockPlan.push({
            survey: surveyKey,
            grade,
            shareholder: recipient.shareholder,
            area: giveAmount,
            type: giveAmount >= surveyArea - TOL ? "whole" : "split",
            reason: "final_sweep",
          });
          surveyRemaining[surveyKey] -= giveAmount;
        } else {
          surveyRemaining[surveyKey] = 0; // Too small to allocate
        }

        break; // Process one survey per iteration
      }
    }

    return blockPlan;
  };

  const planBGradeSurveys = (analysis, shareholderList, plan, dynamicMins) => {
    const bGradePlan = [];

    console.log(
      "\n[B-GRADE ALLOCATION] - Flexible balancing with LIVE entitlement checks"
    );

    for (const grade of B_GRADES) {
      const blocks = analysis.contiguityBlocks[grade] || [];

      for (const block of blocks) {
        const blockArea = block.reduce(
          (sum, s) => sum + agToDec(s.acres, s.ghontay),
          0
        );
        if (blockArea <= TOL) continue;

        const surveyRemaining = {};
        block.forEach((s) => {
          surveyRemaining[s.khasraNo] = agToDec(s.acres, s.ghontay);
        });

        let surveyIndex = 0;

        // Process surveys one by one, always giving to who needs most
        while (surveyIndex < block.length) {
          const survey = block[surveyIndex];
          const surveyArea = surveyRemaining[survey.khasraNo];

          if (surveyArea <= TOL) {
            surveyIndex++;
            continue;
          }

          // FIX 3: Recalculate targets FRESH for each survey (LIVE gaps)
          const targets = shareholderList
            .map((sh) => {
              const entitled = analysis.entitlements[sh].total;
              const currentTotal = plan.allocated[sh];
              const liveGap = entitled - currentTotal;
              return {
                shareholder: sh,
                totalGap: liveGap,
                entitled: entitled,
                currentTotal: currentTotal,
                isPossessor: survey.existingPossessor === sh,
              };
            })
            .filter((t) => t.totalGap > TOL)
            .sort((a, b) => {
              // Possession priority for ties, otherwise by need
              if (Math.abs(a.totalGap - b.totalGap) < 0.5) {
                if (a.isPossessor && !b.isPossessor) return -1;
                if (!a.isPossessor && b.isPossessor) return 1;
              }
              return b.totalGap - a.totalGap;
            });

          if (targets.length === 0) {
            // Everyone is satisfied - shouldn't happen but handle gracefully
            console.log(
              `  [WARNING] No one needs more land but survey ${survey.khasraNo} remains`
            );
            surveyIndex++;
            continue;
          }

          const target = targets[0]; // Most needy shareholder

          // FIX 1: Calculate LIVE needed amount
          const liveNeeded = target.totalGap;
          const give = Math.min(liveNeeded, surveyArea);
          const remainder = surveyArea - give;
          const minThreshold = dynamicMins.STANDARD_MIN;

          // CASE 1: Survey fits entirely within need
          if (surveyArea <= liveNeeded + TOL) {
            bGradePlan.push({
              survey: survey.khasraNo,
              grade,
              shareholder: target.shareholder,
              area: surveyArea,
              type: "whole",
              reason: "b_grade_balance",
            });
            plan.allocated[target.shareholder] += surveyArea;
            plan.allocatedByGrade[target.shareholder][grade] += surveyArea;
            surveyRemaining[survey.khasraNo] = 0;
            surveyIndex++;
          }
          // CASE 2: Can split safely (both pieces >= minThreshold)
          else if (give >= minThreshold && remainder >= minThreshold) {
            bGradePlan.push({
              survey: survey.khasraNo,
              grade,
              shareholder: target.shareholder,
              area: give,
              type: "split",
              reason: "b_grade_split",
            });
            plan.allocated[target.shareholder] += give;
            plan.allocatedByGrade[target.shareholder][grade] += give;
            surveyRemaining[survey.khasraNo] -= give;
            // Don't increment surveyIndex - remainder stays for next iteration
          }
          // CASE 3: Would create micro-split
          else {
            // FIX 2: Check if giving whole would exceed total entitlement significantly
            const wouldTotal = target.currentTotal + surveyArea;
            const wouldExceedBy = wouldTotal - target.entitled;

            if (wouldExceedBy > GRADE_TOL) {
              // Giving whole survey would over-allocate this shareholder
              // Try the next shareholder instead
              const nextTarget = targets.find(
                (t, idx) => idx > 0 && t.totalGap >= surveyArea - GRADE_TOL
              );

              if (nextTarget) {
                // Found someone who can take the whole survey
                bGradePlan.push({
                  survey: survey.khasraNo,
                  grade,
                  shareholder: nextTarget.shareholder,
                  area: surveyArea,
                  type: "whole",
                  reason: "b_redirect_to_needier",
                });
                plan.allocated[nextTarget.shareholder] += surveyArea;
                plan.allocatedByGrade[nextTarget.shareholder][grade] +=
                  surveyArea;
                surveyRemaining[survey.khasraNo] = 0;
                surveyIndex++;
              } else {
                // No one can take it whole without over-allocation
                // Give small piece to current target (even if micro) to make progress
                // This is better than getting stuck
                if (give > ABSOLUTE_FLOOR) {
                  bGradePlan.push({
                    survey: survey.khasraNo,
                    grade,
                    shareholder: target.shareholder,
                    area: give,
                    type: "split",
                    reason: "b_forced_micro_split",
                  });
                  plan.allocated[target.shareholder] += give;
                  plan.allocatedByGrade[target.shareholder][grade] += give;
                  surveyRemaining[survey.khasraNo] -= give;
                } else {
                  // Give whole to most needy, accept small over-allocation
                  bGradePlan.push({
                    survey: survey.khasraNo,
                    grade,
                    shareholder: target.shareholder,
                    area: surveyArea,
                    type: "whole",
                    reason: "b_accept_small_over",
                  });
                  plan.allocated[target.shareholder] += surveyArea;
                  plan.allocatedByGrade[target.shareholder][grade] +=
                    surveyArea;
                  surveyRemaining[survey.khasraNo] = 0;
                  surveyIndex++;
                }
              }
            } else {
              // Over-allocation is within tolerance, give whole survey
              bGradePlan.push({
                survey: survey.khasraNo,
                grade,
                shareholder: target.shareholder,
                area: surveyArea,
                type: "whole",
                reason: "avoid_micro",
              });
              plan.allocated[target.shareholder] += surveyArea;
              plan.allocatedByGrade[target.shareholder][grade] += surveyArea;
              surveyRemaining[survey.khasraNo] = 0;
              surveyIndex++;
            }
          }
        }
      }
    }

    return bGradePlan;
  };

  const buildAllocationPlan = (analysis, shareholderList, dynamicMins) => {
    console.log("\n" + "=".repeat(70));
    console.log("BUILDING ALLOCATION PLAN");
    console.log("=".repeat(70));

    const plan = {
      locked: [],
      aGrades: [],
      bGrades: [],
      allocated: {},
      allocatedByGrade: {},
      lockedSurveyIds: new Set(),
    };

    // Initialize tracking
    shareholderList.forEach((sh) => {
      plan.allocated[sh] = 0;
      plan.allocatedByGrade[sh] = {};
      [...A_GRADES, ...B_GRADES, "C"].forEach((grade) => {
        plan.allocatedByGrade[sh][grade] = 0;
      });
    });

    // Step 1: Locked allocations
    console.log("\n[STEP 1] Locked allocations (C-grade + pre-split)");
    plan.locked = planLockedSurveys(analysis, plan);
    updatePlanTracking(plan, plan.locked);

    shareholderList.forEach((sh) => {
      if (plan.allocated[sh] > 0) {
        console.log(`  ${sh}: ${toAG(plan.allocated[sh])} locked`);
      }
    });

    // Step 2: A-grade allocations
    console.log("\n[STEP 2] A-grade allocations");
    plan.aGrades = planAGradeSurveys(
      analysis,
      shareholderList,
      plan,
      dynamicMins
    );
    updatePlanTracking(plan, plan.aGrades);

    // Step 3: B-grade allocations
    console.log("\n[STEP 3] B-grade allocations (flexible balancing)");
    plan.bGrades = planBGradeSurveys(
      analysis,
      shareholderList,
      plan,
      dynamicMins
    );

    // Final summary
    console.log("\n[ALLOCATION SUMMARY]");
    shareholderList.forEach((sh) => {
      const entitled = analysis.entitlements[sh].total;
      const allocated = plan.allocated[sh];
      const gap = allocated - entitled;
      const status =
        Math.abs(gap) <= TOTAL_TOL ? "OK" : Math.abs(gap) <= 0.1 ? "~" : "!!";
      console.log(
        `  [${status}] ${sh}: ${toAG(allocated)} / ${toAG(entitled)} (${
          gap >= 0 ? "+" : ""
        }${toAG(gap)})`
      );
    });

    return plan;
  };

  // ============================================================================
  // PHASE 2: VERIFY PLAN
  // ============================================================================
  const verifyPlanFeasibility = (
    plan,
    analysis,
    shareholderList,
    dynamicMins
  ) => {
    console.log("\n[VERIFICATION]");

    const verification = {
      feasible: true,
      violations: {
        microSplits: [],
        totalMismatches: [],
        gradeExceeded: [],
      },
    };

    // Check totals
    shareholderList.forEach((sh) => {
      const allocated = plan.allocated[sh];
      const entitled = analysis.entitlements[sh].total;
      const gap = allocated - entitled;

      if (Math.abs(gap) > TOTAL_TOL) {
        verification.violations.totalMismatches.push({
          shareholder: sh,
          allocated,
          entitled,
          gap,
          allocatedAG: toAG(allocated),
          entitledAG: toAG(entitled),
          gapAG: toAG(Math.abs(gap)),
        });
        verification.feasible = false;
      }
    });

    // Check A-grade limits
    shareholderList.forEach((sh) => {
      A_GRADES.forEach((grade) => {
        const allocated = plan.allocatedByGrade[sh][grade];
        const entitled = analysis.entitlements[sh][grade];
        const gap = allocated - entitled;

        if (Math.abs(gap) > GRADE_TOL) {
          const isFromLocked = analysis.lockedSurveys.some(
            (s) => s.existingPossessor === sh && s.grade === grade
          );
          verification.violations.gradeExceeded.push({
            shareholder: sh,
            grade,
            allocated,
            entitled,
            gap,
            isFromLocked,
            allocatedAG: toAG(allocated),
            entitledAG: toAG(entitled),
            gapAG: toAG(Math.abs(gap)),
          });
          if (!isFromLocked) verification.feasible = false;
        }
      });
    });

    console.log(`  Feasible: ${verification.feasible ? "YES" : "NO"}`);
    console.log(
      `  Total mismatches: ${verification.violations.totalMismatches.length}`
    );
    console.log(
      `  A-grade violations: ${verification.violations.gradeExceeded.length}`
    );

    return verification;
  };

  // ============================================================================
  // PHASE 3: EXECUTE PLAN
  // ============================================================================
  const executeVerifiedPlan = (plan) => {
    const allocations = [];

    plan.locked.forEach((alloc) => {
      allocations.push({ ...alloc, executionPhase: "locked" });
    });

    plan.aGrades.forEach((alloc) => {
      allocations.push({ ...alloc, executionPhase: "a-grades" });
    });

    plan.bGrades.forEach((alloc) => {
      allocations.push({ ...alloc, executionPhase: "b-grades" });
    });

    return allocations.filter((a) => a.area > TOL);
  };

  // ============================================================================
  // ITERATIVE BALANCING LOOP - Self-correcting mechanism
  // ============================================================================
  const balanceAllocations = (
    allocations,
    plan,
    analysis,
    shareholderList,
    dynamicMins
  ) => {
    const MAX_BALANCE_ITERATIONS = 10;
    let iteration = 0;
    let balanced = false;

    console.log("\n" + "=".repeat(70));
    console.log("PHASE 3: ITERATIVE BALANCING");
    console.log("=".repeat(70));

    while (!balanced && iteration < MAX_BALANCE_ITERATIONS) {
      iteration++;
      console.log(`\n[BALANCE ITERATION ${iteration}]`);

      // Find who is over and under
      const overAllocated = [];
      const underAllocated = [];

      shareholderList.forEach((sh) => {
        const entitled = analysis.entitlements[sh].total;
        const allocated = plan.allocated[sh];
        const gap = allocated - entitled;

        if (gap > GRADE_TOL) {
          overAllocated.push({
            shareholder: sh,
            excess: gap,
            allocated,
            entitled,
          });
          console.log(
            `  [OVER] ${sh}: ${toAG(allocated)} / ${toAG(entitled)} (+${toAG(
              gap
            )})`
          );
        } else if (gap < -GRADE_TOL) {
          underAllocated.push({
            shareholder: sh,
            deficit: -gap,
            allocated,
            entitled,
          });
          console.log(
            `  [UNDER] ${sh}: ${toAG(allocated)} / ${toAG(entitled)} (-${toAG(
              -gap
            )})`
          );
        }
      });

      if (overAllocated.length === 0 && underAllocated.length === 0) {
        console.log("  All shareholders balanced!");
        balanced = true;
        break;
      }

      if (overAllocated.length === 0 || underAllocated.length === 0) {
        console.log("  [WARNING] Imbalance exists but no transfer possible");
        console.log(
          `    Over: ${overAllocated.length}, Under: ${underAllocated.length}`
        );
        break;
      }

      // Sort by largest imbalance first
      overAllocated.sort((a, b) => b.excess - a.excess);
      underAllocated.sort((a, b) => b.deficit - a.deficit);

      let transferMade = false;

      // Try to transfer from over to under
      // FIX v7.4: Also allow transferring non-locked A-grade (not just B-grade)
      for (const over of overAllocated) {
        if (transferMade) break;

        // Find transferable surveys belonging to over-allocated shareholder
        // Priority: B-grade first, then non-locked A-grade
        const transferableSurveys = allocations
          .filter(
            (a) =>
              a.shareholder === over.shareholder &&
              a.area > 0 &&
              (B_GRADES.includes(a.grade) ||
                (A_GRADES.includes(a.grade) && a.type !== "locked"))
          )
          .sort((a, b) => {
            // B-grade first (easier to transfer)
            const aIsB = B_GRADES.includes(a.grade) ? 0 : 1;
            const bIsB = B_GRADES.includes(b.grade) ? 0 : 1;
            if (aIsB !== bIsB) return aIsB - bIsB;
            // Then by size (smallest first)
            return a.area - b.area;
          });

        for (const survey of transferableSurveys) {
          if (transferMade) break;

          for (const under of underAllocated) {
            // Check if transferring this survey helps
            const transferAmount = Math.min(
              survey.area,
              over.excess,
              under.deficit
            );

            if (transferAmount < ABSOLUTE_FLOOR) continue;

            // Check if we should transfer whole or split
            if (survey.area <= transferAmount + GRADE_TOL) {
              // Transfer whole survey
              console.log(
                `  [TRANSFER] Survey ${survey.survey} (${toAG(survey.area)} ${
                  survey.grade
                })`
              );
              console.log(
                `    From: ${over.shareholder} -> To: ${under.shareholder}`
              );

              // Update allocation record
              survey.shareholder = under.shareholder;
              survey.reason = "balanced_transfer";

              // Update plan tracking
              plan.allocated[over.shareholder] -= survey.area;
              plan.allocated[under.shareholder] += survey.area;
              plan.allocatedByGrade[over.shareholder][survey.grade] -=
                survey.area;
              plan.allocatedByGrade[under.shareholder][survey.grade] +=
                survey.area;

              transferMade = true;
              break;
            } else if (
              transferAmount >= dynamicMins.STANDARD_MIN &&
              survey.area - transferAmount >= dynamicMins.STANDARD_MIN
            ) {
              // Split the survey
              console.log(
                `  [SPLIT-TRANSFER] Survey ${survey.survey}: ${toAG(
                  transferAmount
                )} of ${toAG(survey.area)}`
              );
              console.log(
                `    From: ${over.shareholder} -> To: ${under.shareholder}`
              );

              // Reduce original allocation
              const originalArea = survey.area;
              survey.area = originalArea - transferAmount;

              // Create new allocation for recipient
              allocations.push({
                survey: survey.survey,
                grade: survey.grade,
                shareholder: under.shareholder,
                area: transferAmount,
                type: "split",
                reason: "balanced_split_transfer",
              });

              // Update plan tracking
              plan.allocated[over.shareholder] -= transferAmount;
              plan.allocated[under.shareholder] += transferAmount;
              plan.allocatedByGrade[over.shareholder][survey.grade] -=
                transferAmount;
              plan.allocatedByGrade[under.shareholder][survey.grade] +=
                transferAmount;

              transferMade = true;
              break;
            }
          }
        }
      }

      if (!transferMade) {
        console.log("  [WARNING] Could not find suitable transfer");
        break;
      }
    }

    return {
      balanced,
      iterations: iteration,
      allocations,
    };
  };

  // ============================================================================
  // MAIN ALLOCATION FUNCTION - HYBRID APPROACH
  // ============================================================================
  const performAllocation = () => {
    console.clear();
    console.log("\n" + "=".repeat(70));
    console.log("LAND PARTITION SYSTEM v7.4");
    console.log("Total Entitlement Check + Enhanced Balancing");
    console.log("=".repeat(70));

    const validLandData = landData.filter(
      (land) => land.khasraNo && (land.acres > 0 || land.ghontay > 0)
    );
    const validShareholders = shareholders.filter(
      (sh) => sh.name && sh.sharePercent > 0
    );

    if (validLandData.length === 0 || validShareholders.length === 0) {
      alert("Please enter valid land data and shareholder information");
      return;
    }

    // Validate possessor names
    const shareholderNames = validShareholders.map((sh) =>
      sh.name.trim().toLowerCase()
    );
    const possessorNames = validLandData
      .filter((land) => land.existingPossessor?.trim())
      .map((land) => land.existingPossessor.trim().toLowerCase());

    const unmatchedPossessors = [...new Set(possessorNames)].filter(
      (poss) => !shareholderNames.includes(poss)
    );

    if (unmatchedPossessors.length > 0) {
      alert(
        `NAME MISMATCH:\n\nThese possessor names don't match any shareholder:\n${unmatchedPossessors.join(
          ", "
        )}\n\nPlease ensure names match exactly.`
      );
      return;
    }

    // ========== PHASE 0: ANALYZE ==========
    console.log("\n[PHASE 0] Analyzing problem space...");
    const analysis = analyzeProblemSpace(validLandData, validShareholders);
    const shareholderList = validShareholders.map((sh) => sh.name);
    const dynamicMins = calculateDynamicPracticalMin(analysis, shareholderList);

    // ========== PHASE 1: BUILD INITIAL PLAN ==========
    console.log("\n[PHASE 1] Building initial allocation plan...");
    const plan = buildAllocationPlan(analysis, shareholderList, dynamicMins);

    // ========== PHASE 2: INITIAL VERIFICATION ==========
    console.log("\n[PHASE 2] Verifying initial plan...");
    let verification = verifyPlanFeasibility(
      plan,
      analysis,
      shareholderList,
      dynamicMins
    );

    // ========== PHASE 3: EXECUTE INITIAL PLAN ==========
    let allocations = executeVerifiedPlan(plan);

    // ========== PHASE 4: ITERATIVE BALANCING (if needed) ==========
    let balanceResult = { balanced: verification.feasible, iterations: 0 };

    if (
      !verification.feasible &&
      verification.violations.totalMismatches.length > 0
    ) {
      console.log(
        "\n[PHASE 4] Initial plan has total mismatches - entering balancing loop..."
      );
      balanceResult = balanceAllocations(
        allocations,
        plan,
        analysis,
        shareholderList,
        dynamicMins
      );
      allocations = balanceResult.allocations;

      // Re-verify after balancing
      verification = verifyPlanFeasibility(
        plan,
        analysis,
        shareholderList,
        dynamicMins
      );
    }

    // ========== PHASE 5: FINAL SUMMARY ==========
    console.log("\n" + "=".repeat(70));
    console.log("FINAL RESULT");
    console.log("=".repeat(70));
    console.log(`Balanced: ${verification.feasible ? "YES" : "NO"}`);
    console.log(`Balance iterations: ${balanceResult.iterations}`);
    console.log(
      `Total mismatches: ${verification.violations.totalMismatches.length}`
    );
    console.log(
      `A-grade adjustments: ${verification.violations.gradeExceeded.length}`
    );

    shareholderList.forEach((sh) => {
      const entitled = analysis.entitlements[sh].total;
      const allocated = plan.allocated[sh];
      const gap = allocated - entitled;
      const status =
        Math.abs(gap) <= TOTAL_TOL
          ? "[OK]"
          : Math.abs(gap) <= GRADE_TOL
          ? "[~]"
          : "[!!]";
      console.log(`  ${status} ${sh}: ${toAG(allocated)} / ${toAG(entitled)}`);
    });

    // Calculate final metrics
    const totalAllocated = {};
    const gradeAllocated = {};
    const contiguityMetrics = {};

    shareholderList.forEach((sh) => {
      totalAllocated[sh] = plan.allocated[sh];
      contiguityMetrics[sh] = {};
      GRADES.forEach((grade) => {
        gradeAllocated[`${sh}_${grade}`] = plan.allocatedByGrade[sh][grade];
        contiguityMetrics[sh][grade] = { surveys: [], blocks: 0 };
      });
    });

    allocations.forEach((alloc) => {
      if (
        alloc.area > 0 &&
        !contiguityMetrics[alloc.shareholder][alloc.grade].surveys.includes(
          alloc.survey
        )
      ) {
        contiguityMetrics[alloc.shareholder][alloc.grade].surveys.push(
          alloc.survey
        );
      }
    });

    // Calculate contiguity quality
    const calculateContiguityQuality = (surveys) => {
      if (surveys.length === 0)
        return { quality: "N/A", blocks: 0, fragmentation: 0 };
      if (surveys.length === 1)
        return { quality: "EXCELLENT", blocks: 1, fragmentation: 0 };

      const sorted = [...surveys].sort(
        (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0)
      );
      let blocks = 1;
      for (let i = 1; i < sorted.length; i++) {
        if (
          (parseFloat(sorted[i]) || 0) - (parseFloat(sorted[i - 1]) || 0) >
          CONTIGUITY_GAP
        ) {
          blocks++;
        }
      }

      const fragmentation = Math.round(((blocks - 1) / sorted.length) * 100);
      const quality =
        blocks === 1
          ? "EXCELLENT"
          : blocks === 2
          ? "GOOD"
          : blocks === 3
          ? "ACCEPTABLE"
          : "POOR";

      return { quality, blocks, fragmentation };
    };

    shareholderList.forEach((sh) => {
      GRADES.forEach((grade) => {
        const surveys = contiguityMetrics[sh][grade].surveys;
        if (surveys.length > 0) {
          const quality = calculateContiguityQuality(surveys);
          contiguityMetrics[sh][grade] = { ...quality, surveys };
        }
      });
    });

    // Filter out zero-area allocations
    allocations = allocations.filter((a) => a.area > TOL);

    const results = {
      allocations,
      shareholderList,
      totalEntitled: analysis.entitlements,
      totalAllocated,
      gradeAllocated,
      entitlementOriginal: analysis.entitlements,
      contiguityMetrics,
      verification,
      analysis,
      dynamicMins,
      balanceIterations: balanceResult.iterations,
    };

    setAllocationResults(results);
    setShowResults(true);
  };

  // ============================================================================
  // EXCEL EXPORT - COMPREHENSIVE 7-SHEET OUTPUT
  // ============================================================================
  const generateExcel = () => {
    if (!allocationResults) return;

    const wb = XLSX.utils.book_new();

    const styleHeaderRow = (worksheet, numColumns) => {
      const headerStyle = {
        fill: { fgColor: { rgb: "0A5F38" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
      for (let col = 0; col < numColumns; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellAddress]) worksheet[cellAddress].s = headerStyle;
      }
    };

    const totalDehArea = Object.values(
      allocationResults.analysis.totalByGrade
    ).reduce((a, b) => a + b, 0);

    // ========== SHEET 0: VERIFICATION STATUS ==========
    const sheet0_data = [];
    sheet0_data.push(["Land Partition System "]);

    sheet0_data.push(["Verification Status"]);
    sheet0_data.push([]);

    // Dynamic thresholds info
    if (allocationResults.dynamicMins) {
      const dm = allocationResults.dynamicMins;
      sheet0_data.push(["Dynamic Thresholds:"]);
      sheet0_data.push([
        `  STANDARD_MIN: ${toAG(dm.STANDARD_MIN)} (${Math.round(
          dm.STANDARD_MIN * 40
        )} ghontay)`,
      ]);
      sheet0_data.push([
        `  FAIRNESS_MIN: ${toAG(dm.FAIRNESS_MIN)} (${Math.round(
          dm.FAIRNESS_MIN * 40
        )} ghontay)`,
      ]);
      sheet0_data.push([
        `  ABSOLUTE_FLOOR: ${toAG(dm.ABSOLUTE_FLOOR)} (${Math.round(
          dm.ABSOLUTE_FLOOR * 40
        )} ghontay)`,
      ]);
      sheet0_data.push([]);
    }

    sheet0_data.push([
      "Feasible:",
      allocationResults.verification.feasible ? "YES" : "NO",
    ]);
    sheet0_data.push([
      "Balance Iterations:",
      allocationResults.balanceIterations || 0,
    ]);
    sheet0_data.push([
      "Micro-splits:",
      allocationResults.verification.violations.microSplits?.length || 0,
    ]);
    sheet0_data.push([
      "Total mismatches:",
      allocationResults.verification.violations.totalMismatches.length,
    ]);
    sheet0_data.push([
      "A-grade violations:",
      allocationResults.verification.violations.gradeExceeded.length,
    ]);
    sheet0_data.push([]);
    sheet0_data.push(["Generated:", new Date().toLocaleString()]);

    if (
      !allocationResults.verification.feasible ||
      allocationResults.verification.violations.totalMismatches.length > 0 ||
      allocationResults.verification.violations.gradeExceeded.length > 0
    ) {
      sheet0_data.push([]);
      sheet0_data.push(["DETAILS BELOW"]);
      sheet0_data.push([]);

      if (
        allocationResults.verification.violations.totalMismatches.length > 0
      ) {
        sheet0_data.push(["TOTAL MISMATCHES:"]);
        sheet0_data.push(["Shareholder", "Allocated", "Entitled", "Gap"]);
        allocationResults.verification.violations.totalMismatches.forEach(
          (v) => {
            sheet0_data.push([
              v.shareholder,
              v.allocatedAG,
              v.entitledAG,
              v.gapAG,
            ]);
          }
        );
        sheet0_data.push([]);
      }

      if (allocationResults.verification.violations.gradeExceeded.length > 0) {
        sheet0_data.push(["A-GRADE ADJUSTMENTS:"]);
        sheet0_data.push([
          "Shareholder",
          "Grade",
          "Allocated",
          "Entitled",
          "Difference",
          "Status",
        ]);
        allocationResults.verification.violations.gradeExceeded.forEach((v) => {
          const status = v.isFromLocked
            ? "[Pre-split - unavoidable]"
            : "[Adjusted]";
          sheet0_data.push([
            v.shareholder,
            v.grade,
            v.allocatedAG,
            v.entitledAG,
            (v.gap > 0 ? "+" : "") + v.gapAG,
            status,
          ]);
        });
      }
    } else {
      sheet0_data.push([]);
      sheet0_data.push(["ALL CONSTRAINTS SATISFIED"]);
    }

    const ws0 = XLSX.utils.aoa_to_sheet(sheet0_data);
    ws0["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
    ];
    XLSX.utils.book_append_sheet(wb, ws0, "0. Verification Status");

    // ========== SHEET 1: DEH SUMMARY ==========
    const sheet1_data = [];
    sheet1_data.push(["Grade", "Total Area", "% of Deh", "Survey Numbers"]);

    GRADES.forEach((grade) => {
      const area = allocationResults.analysis.totalByGrade[grade] || 0;
      if (area > 0) {
        // Get survey numbers for this grade
        const surveyNos = [
          ...new Set(
            landData
              .filter((l) => l.grade === grade && l.khasraNo)
              .map((l) => l.khasraNo)
          ),
        ]
          .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
          .join(", ");
        sheet1_data.push([
          grade,
          toAG(area),
          ((area / totalDehArea) * 100).toFixed(1) + "%",
          surveyNos,
        ]);
      }
    });

    sheet1_data.push([]);
    sheet1_data.push(["Total Deh Area", toAG(totalDehArea)]);

    const ws1 = XLSX.utils.aoa_to_sheet(sheet1_data);
    ws1["!cols"] = [{ wch: 10 }, { wch: 15 }, { wch: 12 }, { wch: 60 }];
    styleHeaderRow(ws1, 4);
    XLSX.utils.book_append_sheet(wb, ws1, "1. Deh Summary");

    // ========== SHEET 2: ENTITLEMENTS ==========
    const sheet2_data = [];
    sheet2_data.push(["Shareholder", "Share %", ...GRADES, "Total"]);

    allocationResults.shareholderList.forEach((sh) => {
      const shareInfo = shareholders.find((s) => s.name === sh);
      const row = [
        sh,
        shareInfo ? (shareInfo.sharePercent * 100).toFixed(2) : "0",
      ];
      GRADES.forEach((grade) => {
        const val = allocationResults.analysis.entitlements[sh][grade] || 0;
        row.push(val > 0 ? toAG(val) : "-");
      });
      row.push(toAG(allocationResults.analysis.entitlements[sh].total));
      sheet2_data.push(row);
    });

    const ws2 = XLSX.utils.aoa_to_sheet(sheet2_data);
    ws2["!cols"] = [
      { wch: 22 },
      { wch: 10 },
      ...GRADES.map(() => ({ wch: 10 })),
      { wch: 12 },
    ];
    styleHeaderRow(ws2, GRADES.length + 3);
    XLSX.utils.book_append_sheet(wb, ws2, "2. Entitlements");

    // ========== SHEET 3: SURVEY ALLOCATIONS ==========
    const sheet3_data = [];
    sheet3_data.push([
      "Survey No",
      "Grade",
      "Shareholder",
      "Total Area",
      "Allotted",
      "Possession",
      "Remarks",
    ]);

    // Sort allocations and group by survey
    const sortedAllocations = [...allocationResults.allocations].sort(
      (a, b) => {
        const aNum = parseFloat(a.survey) || 0;
        const bNum = parseFloat(b.survey) || 0;
        return aNum - bNum;
      }
    );

    // OPTION B: Sequential sublabeling across ALL parts of a survey (regardless of grade)
    // Step 1: Group all allocations by BASE survey number (ignoring grade)
    const surveyGroups = {};
    sortedAllocations.forEach((alloc) => {
      const baseSurvey = alloc.survey.includes("/")
        ? alloc.survey.split("/")[0]
        : alloc.survey;
      if (!surveyGroups[baseSurvey]) surveyGroups[baseSurvey] = [];
      surveyGroups[baseSurvey].push(alloc);
    });

    // Step 2: Assign sequential sublabels and build a lookup map for Sheet 4
    const sublabelMap = new Map(); // Map allocation to its sublabel

    Object.entries(surveyGroups).forEach(([baseSurvey, allocs]) => {
      // Sort allocations within survey: by grade first, then by shareholder for consistency
      allocs.sort((a, b) => {
        const gradeOrder = GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade);
        if (gradeOrder !== 0) return gradeOrder;
        return a.shareholder.localeCompare(b.shareholder);
      });

      const hasMultipleParts = allocs.length > 1;

      allocs.forEach((alloc, idx) => {
        // Create unique key for this allocation
        const allocKey = `${alloc.survey}_${alloc.grade}_${alloc.shareholder}_${alloc.area}`;

        // Assign sublabel
        let surveyLabel;
        if (hasMultipleParts) {
          surveyLabel = `${baseSurvey}/${idx + 1}`;
        } else {
          surveyLabel = baseSurvey;
        }

        sublabelMap.set(allocKey, surveyLabel);

        // Find original land entry for total area
        const landEntries = landData.filter((l) => l.khasraNo === baseSurvey);
        const totalArea = landEntries.reduce(
          (sum, l) => sum + agToDec(l.acres, l.ghontay),
          0
        );

        // Check possession
        const isRetained = landEntries.some(
          (l) => l.existingPossessor === alloc.shareholder
        );
        const possessionStatus = isRetained
          ? "Yes"
          : landEntries.some((l) => l.existingPossessor)
          ? "No"
          : "";

        // Build remarks
        const remarks = [];
        if (alloc.type === "locked" && alloc.reason?.includes("c_grade"))
          remarks.push("C-grade");
        if (alloc.type === "locked" && alloc.reason?.includes("pre_split"))
          remarks.push("Pre-split");
        if (isRetained && alloc.type !== "locked") remarks.push("Retained");
        if (hasMultipleParts) remarks.push("Sub-div");

        sheet3_data.push([
          surveyLabel,
          alloc.grade,
          alloc.shareholder,
          toAG(totalArea),
          toAG(alloc.area),
          possessionStatus,
          remarks.join("; "),
        ]);
      });
    });

    // Helper function to get sublabel for an allocation
    const getSublabel = (alloc) => {
      const allocKey = `${alloc.survey}_${alloc.grade}_${alloc.shareholder}_${alloc.area}`;
      return sublabelMap.get(allocKey) || alloc.survey;
    };

    const ws3 = XLSX.utils.aoa_to_sheet(sheet3_data);
    ws3["!cols"] = [
      { wch: 14 },
      { wch: 8 },
      { wch: 22 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 35 },
    ];
    styleHeaderRow(ws3, 7);
    XLSX.utils.book_append_sheet(wb, ws3, "3. Survey Allocations");

    // ========== SHEET 4: PARTITION SUMMARY ==========
    const sheet4_data = [];
    sheet4_data.push([
      "Shareholder",
      "Share %",
      "Entitled",
      "Allotted",
      "Entitled by Grade",
      "Allotted by Grade (with surveys)",
      "Survey Numbers (with areas)",
      "Remarks",
    ]);

    allocationResults.shareholderList.forEach((sh) => {
      const shareInfo = shareholders.find((s) => s.name === sh);
      const entitled = allocationResults.analysis.entitlements[sh].total;
      const allocated = allocationResults.totalAllocated[sh];

      // Entitled by grade
      const entitledByGrade = [];
      GRADES.forEach((grade) => {
        const val = allocationResults.analysis.entitlements[sh][grade] || 0;
        if (val > 0) entitledByGrade.push(`${grade}(${toAG(val)})`);
      });

      // Allotted by grade with surveys - USE SUBLABELS
      const allottedByGrade = [];
      GRADES.forEach((grade) => {
        const gradeAllocs = allocationResults.allocations
          .filter((a) => a.shareholder === sh && a.grade === grade)
          .sort(
            (a, b) => (parseFloat(a.survey) || 0) - (parseFloat(b.survey) || 0)
          );

        if (gradeAllocs.length > 0) {
          const surveyParts = gradeAllocs
            .map((a) => `${getSublabel(a)}(${toAG(a.area)})`)
            .join("+");
          allottedByGrade.push(`${grade}: ${surveyParts}`);
        }
      });

      // All survey numbers with areas - USE SUBLABELS
      const allSurveys = allocationResults.allocations
        .filter((a) => a.shareholder === sh)
        .sort((a, b) => {
          // Sort by base survey number first
          const aBase = parseFloat(a.survey) || 0;
          const bBase = parseFloat(b.survey) || 0;
          if (aBase !== bBase) return aBase - bBase;
          // Then by grade order
          return GRADES.indexOf(a.grade) - GRADES.indexOf(b.grade);
        })
        .map((a) => `${getSublabel(a)}(${toAG(a.area)})`)
        .join(", ");

      // Remarks
      const remarks = [];
      const hasLocked = allocationResults.allocations.some(
        (a) => a.shareholder === sh && a.type === "locked"
      );
      const hasPreSplit = allocationResults.allocations.some(
        (a) => a.shareholder === sh && a.reason?.includes("pre_split")
      );
      if (hasLocked) remarks.push("Locked allocations");
      if (hasPreSplit) remarks.push("Pre-split surveys");

      sheet4_data.push([
        sh,
        shareInfo ? (shareInfo.sharePercent * 100).toFixed(2) : "0",
        toAG(entitled),
        toAG(allocated),
        entitledByGrade.join(", "),
        allottedByGrade.join(", "),
        allSurveys,
        remarks.join(" | "),
      ]);
    });

    const ws4 = XLSX.utils.aoa_to_sheet(sheet4_data);
    ws4["!cols"] = [
      { wch: 22 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 35 },
      { wch: 70 },
      { wch: 60 },
      { wch: 30 },
    ];
    styleHeaderRow(ws4, 8);
    XLSX.utils.book_append_sheet(wb, ws4, "4. Partition Summary");

    // ========== SHEET 5: VERIFICATION ==========
    const sheet5_data = [];
    sheet5_data.push([
      "Shareholder",
      "Category",
      "Allotted / Entitled",
      "Status",
    ]);

    allocationResults.shareholderList.forEach((sh) => {
      const totalEntitled = allocationResults.analysis.entitlements[sh].total;
      const totalAllotted = allocationResults.totalAllocated[sh];
      const diff = Math.abs(totalAllotted - totalEntitled);

      const status =
        diff <= TOTAL_TOL
          ? "[OK] Perfect"
          : diff <= 0.1
          ? "[~] Close"
          : "[!!] Mismatch";
      sheet5_data.push([
        sh,
        "TOTAL",
        `${toAG(totalAllotted)} / ${toAG(totalEntitled)}`,
        status,
      ]);

      // Grade-wise breakdown
      GRADES.forEach((grade) => {
        const entitled =
          allocationResults.analysis.entitlements[sh][grade] || 0;
        const key = `${sh}_${grade}`;
        const allotted = allocationResults.gradeAllocated[key] || 0;

        if (entitled > 0 || allotted > 0) {
          const gradeDiff = Math.abs(allotted - entitled);
          const gradeStatus = A_GRADES.includes(grade)
            ? gradeDiff <= GRADE_TOL
              ? "[OK]"
              : "[!!]"
            : gradeDiff <= 0.5
            ? "[OK]"
            : "[~]";

          sheet5_data.push([
            "",
            grade,
            `${toAG(allotted)} / ${toAG(entitled)}`,
            gradeStatus,
          ]);
        }
      });
    });

    sheet5_data.push([]);
    sheet5_data.push([]);

    // Deh total check
    const totalAllocatedArea = allocationResults.allocations.reduce(
      (sum, a) => sum + a.area,
      0
    );
    const dehDiff = Math.abs(totalAllocatedArea - totalDehArea);
    const dehStatus = dehDiff <= TOTAL_TOL ? "[OK] Perfect" : "[!!] MISMATCH";
    sheet5_data.push([
      "TOTAL DEH",
      "Area Check",
      `${toAG(totalAllocatedArea)} / ${toAG(totalDehArea)}`,
      dehStatus,
    ]);

    const ws5 = XLSX.utils.aoa_to_sheet(sheet5_data);
    ws5["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 25 }, { wch: 15 }];
    styleHeaderRow(ws5, 4);
    XLSX.utils.book_append_sheet(wb, ws5, "5. Verification");

    // ========== SHEET 6: CONTIGUITY ANALYSIS ==========
    const sheet6_data = [];
    sheet6_data.push([
      "Shareholder",
      "Grade",
      "Total Surveys",
      "Maintained (Whole)",
      "Divided (Split)",
      "Contiguous Blocks",
      "Fragmentation %",
      "Quality Rating",
      "Survey Numbers",
    ]);

    allocationResults.shareholderList.forEach((sh) => {
      GRADES.forEach((grade) => {
        // Get all allocations for this shareholder and grade
        const gradeAllocs = allocationResults.allocations.filter(
          (a) => a.shareholder === sh && a.grade === grade
        );

        if (gradeAllocs.length > 0) {
          // Count unique base surveys
          const baseSurveys = new Set();
          gradeAllocs.forEach((a) => {
            const base = a.survey.includes("/")
              ? a.survey.split("/")[0]
              : a.survey;
            baseSurveys.add(base);
          });

          // Count maintained vs divided
          let maintained = 0;
          let divided = 0;
          baseSurveys.forEach((baseSurvey) => {
            // Check if this survey was split (multiple allocations to different shareholders)
            const allAllocsForSurvey = allocationResults.allocations.filter(
              (a) => {
                const basA = a.survey.includes("/")
                  ? a.survey.split("/")[0]
                  : a.survey;
                return basA === baseSurvey && a.grade === grade;
              }
            );
            if (allAllocsForSurvey.length > 1) {
              divided++;
            } else {
              maintained++;
            }
          });

          // Get survey labels with sublabels
          const surveyLabels = gradeAllocs
            .map((a) => getSublabel(a))
            .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));

          const surveyStr = surveyLabels.join(", ");

          // Calculate contiguity blocks
          const sortedBase = [...baseSurveys].sort(
            (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0)
          );
          let blocks = 1;
          for (let i = 1; i < sortedBase.length; i++) {
            if (
              (parseFloat(sortedBase[i]) || 0) -
                (parseFloat(sortedBase[i - 1]) || 0) >
              CONTIGUITY_GAP
            ) {
              blocks++;
            }
          }

          const fragmentation =
            baseSurveys.size > 1
              ? Math.round(((blocks - 1) / baseSurveys.size) * 100)
              : 0;
          const quality =
            blocks === 1
              ? "EXCELLENT"
              : blocks === 2
              ? "GOOD"
              : blocks === 3
              ? "ACCEPTABLE"
              : "POOR";

          sheet6_data.push([
            sh,
            grade,
            baseSurveys.size,
            maintained,
            divided,
            blocks,
            fragmentation + "%",
            quality,
            surveyStr,
          ]);
        }
      });
    });

    // Deh-level analysis
    sheet6_data.push([]);
    sheet6_data.push([]);
    sheet6_data.push(["DEH-LEVEL SURVEY ANALYSIS"]);
    sheet6_data.push([]);

    // Count unique surveys and divided surveys
    const allUniqueSurveys = new Set();
    const dividedSurveysMap = {};

    landData.forEach((land) => {
      if (land.khasraNo) allUniqueSurveys.add(land.khasraNo);
    });

    // Check which surveys are divided (multiple shareholders)
    allUniqueSurveys.forEach((surveyNo) => {
      const allocsForSurvey = allocationResults.allocations.filter((a) => {
        const basA = a.survey.includes("/") ? a.survey.split("/")[0] : a.survey;
        return basA === surveyNo;
      });
      const uniqueShareholders = [
        ...new Set(allocsForSurvey.map((a) => a.shareholder)),
      ];
      if (uniqueShareholders.length > 1) {
        dividedSurveysMap[surveyNo] = uniqueShareholders.sort();
      }
    });

    const totalSurveys = allUniqueSurveys.size;
    const totalDivided = Object.keys(dividedSurveysMap).length;
    const totalMaintained = totalSurveys - totalDivided;

    sheet6_data.push(["Total Surveys in Deh:", totalSurveys]);
    sheet6_data.push(["Total Area:", toAG(totalDehArea)]);
    sheet6_data.push(["Maintained (Whole):", totalMaintained]);
    sheet6_data.push(["Divided (Split):", totalDivided]);

    if (totalDivided > 0) {
      sheet6_data.push([]);
      sheet6_data.push(["DIVIDED SURVEYS DETAIL"]);
      sheet6_data.push([
        "Survey No.",
        "Total Area",
        "Parts",
        "Allocated To (Shareholders)",
      ]);

      Object.keys(dividedSurveysMap)
        .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
        .forEach((surveyNo) => {
          const landEntries = landData.filter((l) => l.khasraNo === surveyNo);
          const surveyArea = landEntries.reduce(
            (sum, l) => sum + agToDec(l.acres, l.ghontay),
            0
          );
          const shareholders = dividedSurveysMap[surveyNo];
          sheet6_data.push([
            surveyNo,
            toAG(surveyArea),
            shareholders.length,
            shareholders.join(", "),
          ]);
        });
    }

    const ws6 = XLSX.utils.aoa_to_sheet(sheet6_data);
    ws6["!cols"] = [
      { wch: 22 },
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 50 },
    ];
    styleHeaderRow(ws6, 9);
    XLSX.utils.book_append_sheet(wb, ws6, "6. Contiguity Analysis");

    // Save file
    XLSX.writeFile(
      wb,
      `Land_Partition_System_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // ============================================================================
  // DATA HANDLERS
  // ============================================================================
  const addLandRow = () => {
    setLandData([
      ...landData,
      {
        khasraNo: "",
        acres: 0,
        ghontay: 0,
        grade: "A1",
        existingPossessor: "",
      },
    ]);
  };

  const updateLandRow = (index, field, value) => {
    const newData = [...landData];
    if (field === "acres" || field === "ghontay") {
      newData[index][field] = parseFloat(value) || 0;
    } else {
      newData[index][field] = value;
    }
    setLandData(newData);
  };

  const removeLandRow = (index) => {
    if (landData.length > 1)
      setLandData(landData.filter((_, i) => i !== index));
  };

  const addShareholder = () => {
    setShareholders([...shareholders, { name: "", sharePercent: 0 }]);
  };

  const updateShareholder = (index, field, value) => {
    const newData = [...shareholders];
    if (field === "sharePercent") {
      newData[index][field] =
        Math.round((parseFloat(value) || 0) * 1000000) / 1000000;
    } else {
      newData[index][field] = value;
    }
    setShareholders(newData);
  };

  const removeShareholder = (index) => {
    if (shareholders.length > 1)
      setShareholders(shareholders.filter((_, i) => i !== index));
  };

  // ============================================================================
  // RESULTS DISPLAY - RESPONSIVE
  // ============================================================================
  const ResultsDisplay = () => {
    if (!allocationResults) return null;

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Status Banner */}
        <div
          className={`rounded-lg p-3 sm:p-4 ${
            allocationResults.verification.feasible
              ? "bg-green-50 border border-green-200"
              : "bg-yellow-50 border border-yellow-200"
          }`}
        >
          <div className="flex items-center">
            {allocationResults.verification.feasible ? (
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mr-2 sm:mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mr-2 sm:mr-3 flex-shrink-0" />
            )}
            <div>
              <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                {allocationResults.verification.feasible
                  ? "Partition Complete"
                  : "Partition Complete (with adjustments)"}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                {allocationResults.verification.violations.gradeExceeded
                  .length > 0
                  ? `${allocationResults.verification.violations.gradeExceeded.length} A-grade adjustments`
                  : "All constraints satisfied"}
              </p>
            </div>
          </div>
        </div>

        {/* Summary - Mobile Card View */}
        <div className="block sm:hidden space-y-3">
          <h3 className="text-base font-semibold text-gray-900 px-1">
            Allocation Summary
          </h3>
          {allocationResults.shareholderList.map((sh, idx) => {
            const entitled = allocationResults.analysis.entitlements[sh].total;
            const allocated = allocationResults.totalAllocated[sh];
            const diff = Math.abs(allocated - entitled);

            return (
              <div key={idx} className="bg-white rounded-lg shadow p-3">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-gray-900 text-sm">
                    {sh}
                  </span>
                  {diff <= TOTAL_TOL ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-semibold">
                      Perfect
                    </span>
                  ) : diff <= 0.1 ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 font-semibold">
                      Close
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                      Gap
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Entitled:</span>
                    <span className="ml-1 font-medium">{toAG(entitled)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Allocated:</span>
                    <span className="ml-1 font-medium">{toAG(allocated)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Table - Desktop */}
        <div className="hidden sm:block bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Allocation Summary
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Shareholder
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Entitled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Allocated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allocationResults.shareholderList.map((sh, idx) => {
                  const entitled =
                    allocationResults.analysis.entitlements[sh].total;
                  const allocated = allocationResults.totalAllocated[sh];
                  const diff = Math.abs(allocated - entitled);

                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {sh}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {toAG(entitled)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {toAG(allocated)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {diff <= TOTAL_TOL ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-semibold">
                            Perfect
                          </span>
                        ) : diff <= 0.1 ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-semibold">
                            Close
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                            Gap
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Download Button */}
        <div className="flex justify-center">
          <button
            onClick={generateExcel}
            className="flex items-center px-4 sm:px-6 py-2.5 sm:py-3 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] transition-colors shadow-lg text-sm sm:text-base"
          >
            <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Download Results
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER - FULLY RESPONSIVE
  // ============================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header - Responsive */}
      <div className="fixed top-0 left-0 right-0 bg-[#0A5F38] text-white shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => {
                  onBackToLanding();
                  setShowResults(false);
                  setCurrentStep(1);
                }}
                className="p-1.5 sm:p-2 hover:bg-[#084d2d] rounded-lg transition-colors"
              >
                <HomeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </button>
              <div>
                <h1 className="text-base sm:text-xl font-bold">
                  E-Partition System
                </h1>
              </div>
            </div>
            {!showResults && (
              <div className="flex items-center space-x-1 sm:space-x-2">
                {[1, 2].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base ${
                      currentStep === step
                        ? "bg-white text-[#0A5F38]"
                        : currentStep > step
                        ? "bg-green-400 text-white"
                        : "bg-green-800 text-green-300"
                    }`}
                  >
                    {currentStep > step ? "✓" : step}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Responsive */}
      <div className="pt-16 sm:pt-20 pb-16 sm:pb-12 min-h-screen">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          {!showResults ? (
            <>
              {/* Info Banner - Responsive */}
              <div className="mb-4 sm:mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm text-blue-700">
                      Enter Survey No, Area, Grade, and Possessor. The system
                      auto-detects pre-splits and bundles.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 1: Land Data - Responsive */}
              {currentStep === 1 && (
                <div className="space-y-4 sm:space-y-6">
                  {/* Upload Section */}
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
                      <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                      Upload Excel File
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <label className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors shadow-md text-sm sm:text-base">
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Choose File
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </label>
                      <button
                        onClick={downloadSampleExcel}
                        className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md text-sm sm:text-base"
                      >
                        <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Download Template
                      </button>
                    </div>
                    {uploadedFileName && (
                      <div className="mt-3 sm:mt-4 flex items-center text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="font-medium truncate">
                          {uploadedFileName}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Manual Entry - Mobile Card View + Desktop Table */}
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        Land Data
                      </h3>
                      <button
                        onClick={addLandRow}
                        className="flex items-center justify-center px-3 sm:px-4 py-2 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                        Add Survey
                      </button>
                    </div>

                    {/* Mobile Card View */}
                    <div className="block sm:hidden p-3 space-y-3">
                      {landData.map((land, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-3 bg-gray-50"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-semibold text-gray-500">
                              Survey #{index + 1}
                            </span>
                            <button
                              onClick={() => removeLandRow(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              disabled={landData.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500">
                                Survey No
                              </label>
                              <input
                                type="text"
                                value={land.khasraNo}
                                onChange={(e) =>
                                  updateLandRow(
                                    index,
                                    "khasraNo",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                                placeholder="Survey #"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">
                                Grade
                              </label>
                              <select
                                value={land.grade}
                                onChange={(e) =>
                                  updateLandRow(index, "grade", e.target.value)
                                }
                                className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                              >
                                {GRADES.map((g) => (
                                  <option key={g} value={g}>
                                    {g}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">
                                Acres
                              </label>
                              <input
                                type="number"
                                value={land.acres}
                                onChange={(e) =>
                                  updateLandRow(index, "acres", e.target.value)
                                }
                                className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                                min="0"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">
                                Ghontay
                              </label>
                              <input
                                type="number"
                                value={land.ghontay}
                                onChange={(e) =>
                                  updateLandRow(
                                    index,
                                    "ghontay",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                                min="0"
                                max="39"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs text-gray-500">
                                Possessor
                              </label>
                              <input
                                type="text"
                                value={land.existingPossessor}
                                onChange={(e) =>
                                  updateLandRow(
                                    index,
                                    "existingPossessor",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                                placeholder="Possessor name"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block p-6 overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Survey No
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Acres
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Ghontay
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Grade
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Possessor
                            </th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {landData.map((land, index) => (
                            <tr
                              key={index}
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={land.khasraNo}
                                  onChange={(e) =>
                                    updateLandRow(
                                      index,
                                      "khasraNo",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                  placeholder="Survey #"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  value={land.acres}
                                  onChange={(e) =>
                                    updateLandRow(
                                      index,
                                      "acres",
                                      e.target.value
                                    )
                                  }
                                  className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                  min="0"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  value={land.ghontay}
                                  onChange={(e) =>
                                    updateLandRow(
                                      index,
                                      "ghontay",
                                      e.target.value
                                    )
                                  }
                                  className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                  min="0"
                                  max="39"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <select
                                  value={land.grade}
                                  onChange={(e) =>
                                    updateLandRow(
                                      index,
                                      "grade",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                >
                                  {GRADES.map((g) => (
                                    <option key={g} value={g}>
                                      {g}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={land.existingPossessor}
                                  onChange={(e) =>
                                    updateLandRow(
                                      index,
                                      "existingPossessor",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                  placeholder="Possessor name"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => removeLandRow(index)}
                                  className="text-red-600 hover:text-red-800"
                                  disabled={landData.length === 1}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Shareholders - Responsive */}
              {currentStep === 2 && (
                <div className="bg-white rounded-lg shadow">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                      Shareholders
                    </h3>
                    <button
                      onClick={addShareholder}
                      className="flex items-center justify-center px-3 sm:px-4 py-2 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] text-sm"
                    >
                      <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                      Add Shareholder
                    </button>
                  </div>

                  {/* Mobile Card View */}
                  <div className="block sm:hidden p-3 space-y-3">
                    {shareholders.map((sh, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-semibold text-gray-500">
                            Shareholder #{index + 1}
                          </span>
                          <button
                            onClick={() => removeShareholder(index)}
                            className="text-red-600 hover:text-red-800 p-1"
                            disabled={shareholders.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-gray-500">
                              Name
                            </label>
                            <input
                              type="text"
                              value={sh.name}
                              onChange={(e) =>
                                updateShareholder(index, "name", e.target.value)
                              }
                              className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                              placeholder="Full Name"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">
                              Share %
                            </label>
                            <input
                              type="number"
                              value={
                                sh.sharePercent > 0
                                  ? Math.round(sh.sharePercent * 100 * 1000) /
                                    1000
                                  : ""
                              }
                              onChange={(e) =>
                                updateShareholder(
                                  index,
                                  "sharePercent",
                                  parseFloat(e.target.value || 0) / 100
                                )
                              }
                              className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-[#0A5F38]"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden sm:block p-6">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Share %
                          </th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {shareholders.map((sh, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={sh.name}
                                onChange={(e) =>
                                  updateShareholder(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                placeholder="Full Name"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="number"
                                value={
                                  sh.sharePercent > 0
                                    ? Math.round(sh.sharePercent * 100 * 1000) /
                                      1000
                                    : ""
                                }
                                onChange={(e) =>
                                  updateShareholder(
                                    index,
                                    "sharePercent",
                                    parseFloat(e.target.value || 0) / 100
                                  )
                                }
                                className="w-32 px-2 py-1 border rounded focus:ring-2 focus:ring-[#0A5F38]"
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <button
                                onClick={() => removeShareholder(index)}
                                className="text-red-600 hover:text-red-800"
                                disabled={shareholders.length === 1}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mx-3 sm:mx-6 mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total:</span>
                      <span
                        className={`text-sm font-semibold ${
                          Math.abs(
                            shareholders.reduce(
                              (sum, sh) => sum + (sh.sharePercent || 0),
                              0
                            ) *
                              100 -
                              100
                          ) < 0.01
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {(
                          shareholders.reduce(
                            (sum, sh) => sum + (sh.sharePercent || 0),
                            0
                          ) * 100
                        ).toFixed(2)}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation - Responsive */}
              <div className="mt-6 sm:mt-8 flex justify-between gap-3">
                <button
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  className="flex items-center px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm sm:text-base"
                  disabled={currentStep === 1}
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Back</span>
                </button>

                {currentStep < 2 ? (
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="flex items-center px-4 sm:px-6 py-2.5 sm:py-3 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] shadow-lg text-sm sm:text-base"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2" />
                  </button>
                ) : (
                  <button
                    onClick={performAllocation}
                    className="flex items-center px-4 sm:px-6 py-2.5 sm:py-3 bg-[#0A5F38] text-white rounded-lg hover:bg-[#084d2d] shadow-lg text-sm sm:text-base"
                  >
                    <Brain className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Generate Partition</span>
                    <span className="sm:hidden">Generate</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Results Header - Responsive */}
              <div className="mb-4 sm:mb-8 bg-white rounded-lg shadow p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      Partition Results
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowResults(false);
                      setCurrentStep(1);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm sm:text-base"
                  >
                    Back to Input
                  </button>
                </div>
              </div>
              <ResultsDisplay />
            </>
          )}
        </div>
      </div>

      {/* Footer - Responsive */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#0A5F38] py-2 shadow-lg z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <p className="text-[10px] sm:text-xs text-center text-green-100">
            <span className="hidden sm:inline">Developed by </span>
            <span className="font-semibold text-white">
              Ghazanfar Perdakh, PAS
            </span>
            <span className="hidden sm:inline">
              {" "}
              - Assistant Commissioner, Govt of Sindh
            </span>{" "}
            © 2025 , gperdakh@gmail.com
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PartitionSystem;
